import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import * as Cesium from 'cesium';
import 'cesium/Build/Cesium/Widgets/widgets.css';
import { useGrpcService } from '../hooks/useGrpcService';
import { useAuth } from '../contexts/AuthContext';
import { ControlPanelProvider, useControlPanel } from '../contexts/ControlPanelContext';
import { HighlightModeProvider } from '../contexts/HighlightModeContext';
import { ProjectDataProvider } from '../contexts/ProjectDataContext';
import { ArtifactActionsProvider } from '../contexts/ArtifactActionsContext';
import { ControlPanel } from './ControlPanel';
import { InfoPanel } from './InfoPanel';
import { ScreenOverlay } from './ScreenOverlay';
import { DirectionsPanel } from './DirectionsPanel';
import { OnboardingTour } from './onboarding/OnboardingTour';
import { AccountModal } from './AccountModal';
import { AccountInfoModal } from './AccountInfoModal';
import { LoginModal } from './LoginModal';
import { CreateProjectModal } from './CreateProjectModal';
import { PolygonDrawing } from './PolygonDrawing';
import { ReportModal } from './ReportModal';
import { DownloadModal } from './DownloadModal';
import { EditProjectModal } from './EditProjectModal';
import { RemoveProjectConfirmModal } from './RemoveProjectConfirmModal';
import { ProjectSettingsModal } from './ProjectSettingsModal';
import LegendOverlay from './LegendOverlay';
import { MapAttribution } from './MapAttribution';
import PrintMode from './PrintMode';
import { DemoRestrictionModal } from './DemoRestrictionModal';
import { CreateProjectConfirmation } from './billing/CreateProjectConfirmation';
import { collectDrawnFeatures, parseSavedFeaturePositions, parseSavedFeatureStyle } from '../utils/legendData';
import { MapControlPanel } from './MapControlPanel';
import { useProjectBoundsWithViewMode } from '../hooks/useProjectBoundsWithViewMode';
import { useContourHighlight } from '../hooks/useContourHighlight';
import { applyMonochromeContours, restoreBakedContours, HIGHLIGHTED_CONTOUR_FLAG } from '../hooks/artifacts/contourMonochrome';
import { useStreamHighlight } from '../hooks/useStreamHighlight';
import { useMeasure } from '../hooks/useMeasure';
import { PathElevationPopout } from './PathTool/PathElevationPopout';
import { MapOverlays } from './MapViewer/MapOverlays';
import { ToolDialogs } from './MapViewer/ToolDialogs';
import { ServerArtifactDialogs } from './MapViewer/ServerArtifactDialogs';
import { ConfirmationPrompts } from './MapViewer/ConfirmationPrompts';
import { usePaths } from '../hooks/usePaths';
import { setLayerReadiness } from '../hooks/useLayerReadiness';
import { readyAnalyticalLayers } from '../utils/colorByOptions';
import { usePlacemarks } from '../hooks/usePlacemarks';
import { useSketch } from '../hooks/useSketch';
import { useLabels } from '../hooks/useLabels';
import { LabelInlineEditor, LabelMapOverlay, LabelStylePopout, DEFAULT_LABEL_STYLE } from './LabelTool';
import { usePolygons } from '../hooks/usePolygons';
import { useMarkupMode } from '../hooks/useMarkupMode';
import { useScopes } from '../hooks/useScopes';
import { usePolygonStats } from '../hooks/usePolygonStats';
import { useUserFolders } from '../hooks/useUserFolders';
import { useServerArtifactRenderer } from '../hooks/useServerArtifactRenderer';
import { useServerArtifactView } from '../hooks/useServerArtifactView';
import { useLayerTypeDetection } from '../hooks/useLayerTypeDetection';
import { usePlacemarkClustering } from '../hooks/usePlacemarkClustering';
import { ToolDrawPanel } from './MarkupMode/ToolDrawPanel';
import { CanvasBottomChrome } from './MarkupMode/CanvasBottomChrome';
import { SketchToolPanel } from './MarkupMode/SketchToolPanel';
import { SketchSaveModal } from './MarkupMode/SketchSaveModal';
import { DEFAULT_COLOR, TOOL_COLORS } from '../constants/colors';
import { MarkupUploadModal } from './MarkupMode/MarkupUploadModal';
import { useUserProjectsContext } from '../contexts/UserProjectsContext';
import { projectService } from '../services/projectService';
import { useMapViewerModals } from './MapViewer/hooks/useMapViewerModals';
import { useEventSubscribe, eventBus } from '../events';
import { canModifyArtifact, capabilitiesForProject, hasFullAccess } from '../utils/capabilities';
import { readCommentsVisible } from '../utils/commentsVisibility';
import { useProfileCrosshair } from '../hooks/useProfileCrosshair';
import { useBufferConfig } from '../hooks/useBufferConfig';
import { DemoGateOnCreateModal } from './DemoGateOnCreateModal';
import { GlobalAtlasModal } from './GlobalAtlasModal';
import { KeyboardShortcutsModal } from './KeyboardShortcutsModal';
import { LowResScoutingPill } from './LowResScoutingPill';
import { useDemUploadFlow } from '../hooks/useDemUploadFlow';
import { useAuthNavigation } from '../hooks/useAuthNavigation';
import { useCursorManager } from '../hooks/useCursorManager';
import { useToolKeyboardShortcuts } from '../hooks/useToolKeyboardShortcuts';
import { useGlobalKeyboardShortcuts } from '../hooks/useGlobalKeyboardShortcuts';
import { useProjectPolygonDrawing } from '../hooks/useProjectPolygonDrawing';
import { useDraftCommentPromotion } from '../hooks/useDraftCommentPromotion';
import { useComments } from '../hooks/useComments';
import { useCommentPins } from '../hooks/useCommentPins';
import { useProjectCollaborators } from '../hooks/useProjectCollaborators';
import { useProjectCommentTags } from '../hooks/useProjectCommentTags';
import { CommentThreadPopover } from './Comments/CommentThreadPopover';
import { CommentDraftInline } from './Comments/CommentDraftInline';
import { CommentHtmlPin } from './Comments/CommentHtmlPin';
import { PopoverLeaderLine } from './Comments/PopoverLeaderLine';
import { useUnsavedItemsFacade } from '../hooks/useUnsavedItemsFacade';
import { useArtifactPromotion } from '../hooks/useArtifactPromotion';
import { useUnitPreferences } from '../hooks/useUnitPreferences';
import { useServerArtifactEdit } from '../hooks/useServerArtifactEdit';
import { userFolderService } from '../services/userFolderService';
import { useCesiumEventRouter } from '../hooks/useCesiumEventRouter';
import { messageFromSaveError } from '../utils/saveErrorMessage';
import { X } from 'lucide-react';
// Cesium Ion Asset IDs
const BING_ARIAL_ASSET_ID = 2;
// Name given to a sketch saved straight from the Unsaved Drawings row, where
// there is no name field. Matches the Finish modal's default.
const SKETCH_ARTIFACT_NAME = 'Sketch';
// Inner component that can access the ControlPanel context
const MapViewerWithContext = ({ viewer, grpcService, isPolygonDrawing, 
// Consolidated modal state/actions
modalState, modalActions, 
// Project refresh function
refetchUserProjects, 
// DEM upload state
isDemUploading, 
// Markup mode props
activeTool, onSetTool, 
// Unsaved items
unsavedItems, onSaveItem, onDeleteItem, onToggleUnsavedVisibility, isUnsavedItemVisible, 
// Scopes
scopes, scopesLoading, activeScopeId, onCaptureScope, onApplyScope, onClearScope, onDeleteScope, onOverrideScope, onRenameScope, onDuplicateScope, onReorderScopes, 
// User folders
userFolders, onShowAccountModal, 
// Project bounds toggle
boundsVisible, onToggleBoundsVisible, onShowInfo, onFlyToArtifact, onEditArtifact, onDuplicateArtifact, onMoveArtifact, }) => {
    var _a;
    const { state: controlPanelState, actions: controlPanelActions } = useControlPanel();
    const { isAuthenticated } = useAuth();
    const { projects: userProjects } = useUserProjectsContext();
    // Listen for control panel collapse events from markup mode
    useEventSubscribe('controlPanel:collapse', () => {
        controlPanelActions.collapsePanel();
    });
    // Return the panel to its main view (expanded) — the tour uses this so its
    // main-view stops are visible even when the user is inside a project.
    useEventSubscribe('controlPanel:switchToMain', () => {
        if (controlPanelState.isCollapsed) {
            controlPanelActions.toggleCollapsed();
        }
        controlPanelActions.switchToMainMode();
    });
    // Handle edit project modal
    const handleEditProject = useCallback((project) => {
        modalActions.openEditProject(project);
    }, [modalActions]);
    // Handle remove project modal
    const handleRemoveProject = useCallback((project) => {
        modalActions.openRemoveProject(project);
    }, [modalActions]);
    // Handle settings modal
    const handleOpenSettings = useCallback((project) => {
        modalActions.openSettings(project);
    }, [modalActions]);
    // Handle save project
    const handleSaveProject = useCallback(async (projectId, name, description) => {
        try {
            const result = await projectService.updateProject(projectId, name, description);
            if (result.success) {
                refetchUserProjects();
                modalActions.closeModal();
                eventBus.emit('notification:status', { message: 'Project updated successfully' });
            }
            else {
                eventBus.emit('notification:error', { message: `Update failed: ${result.message}` });
            }
        }
        catch (error) {
            console.error('Failed to update project:', error);
            eventBus.emit('notification:error', { message: `Failed to update project: ${error.message || 'Unknown error'}` });
        }
    }, [refetchUserProjects, modalActions]);
    // Handle confirm remove project
    const handleConfirmRemoveProject = useCallback(async (projectId) => {
        try {
            const result = await projectService.removeProject(projectId);
            if (result.success) {
                refetchUserProjects();
                modalActions.closeModal();
                eventBus.emit('notification:status', { message: 'Project removed successfully' });
            }
            else {
                eventBus.emit('notification:error', { message: `Remove failed: ${result.message}` });
            }
        }
        catch (error) {
            console.error('Failed to remove project:', error);
            eventBus.emit('notification:error', { message: `Failed to remove project: ${error.message || 'Unknown error'}` });
        }
    }, [refetchUserProjects, modalActions]);
    // Listen for global open event from Project Structure
    useEventSubscribe('modal:openReport', (payload) => {
        modalActions.openReport(payload.statistics || null, payload.fallbackPropertyBounds || null);
    }, [modalActions]);
    // On ready status, show confirmation modal instead of auto-open
    useEventSubscribe('download:status', (payload) => {
        if (payload.status === 'ready' && payload.url) {
            modalActions.openDownload(payload.url, payload.downloadId || '');
        }
    }, [modalActions]);
    // If a download is consumed elsewhere (e.g., clicking the green button), close this modal if it matches
    useEventSubscribe('download:consume', (payload) => {
        if (payload.downloadId && payload.downloadId === modalState.readyDownloadId) {
            modalActions.closeDownload();
        }
    }, [modalState.readyDownloadId, modalActions]);
    // Listen for global request to show Account modal (e.g., from CreateProjectModal gating)
    useEventSubscribe('modal:showAccountModal', () => {
        modalActions.openAccount();
    }, [modalActions]);
    // Listen for global request to open Create Project modal (e.g., from DemoRestrictionModal)
    useEventSubscribe('modal:openCreateProject', () => {
        modalActions.openCreateProject();
    }, [modalActions]);
    // Reload the artifacts currently rendered on the map (re-fetch their imagery).
    // Fired when a full-resolution re-render completes (e.g. after an Atlas unlock of
    // a previously low-res scouting project) so the cached low-res tiles are replaced
    // by full-res without the user having to reopen the project.
    useEventSubscribe('data:reloadLoadedArtifacts', () => {
        grpcService.loadedArtifacts.forEach((_data, artifactId) => {
            if (typeof grpcService.reloadArtifact === 'function') {
                grpcService.reloadArtifact(artifactId, true).catch(() => { });
            }
        });
    }, [grpcService]);
    // Open an existing project by id (proximity redirect from the create flow).
    // Mirrors MyProjectsSection's click-to-open: build the Project adapter the
    // selection flow expects, select it (flies the camera + loads structure),
    // then switch the control panel into project-structure view so the user
    // lands inside the project rather than back on the list.
    useEventSubscribe('project:open', ({ projectId }) => {
        const up = userProjects.find(p => p.id === projectId);
        if (!up)
            return;
        const projectObject = {
            getId: () => up.id,
            getName: () => up.name,
            getBounds: () => {
                if (up.requestedBounds && up.requestedBounds.length >= 3) {
                    const lats = up.requestedBounds.map((c) => c.lat);
                    const lngs = up.requestedBounds.map((c) => c.lng);
                    return {
                        getWest: () => Math.min(...lngs),
                        getSouth: () => Math.min(...lats),
                        getEast: () => Math.max(...lngs),
                        getNorth: () => Math.max(...lats),
                    };
                }
                return null;
            },
            getRequestedBoundsList: () => {
                if (up.requestedBounds && up.requestedBounds.length >= 3) {
                    return up.requestedBounds.map((c) => ({
                        getLatitude: () => c.lat,
                        getLongitude: () => c.lng,
                    }));
                }
                return [];
            },
        };
        grpcService.selectProject(projectObject);
        eventBus.emit('controlPanel:switchToProjectStructure');
    }, [userProjects, grpcService]);
    // Removed transient status message system - too noisy for users
    return (_jsxs(_Fragment, { children: [_jsx(LowResScoutingPill, { project: controlPanelState.viewMode === 'project-structure'
                    ? ((_a = userProjects.find(up => { var _a, _b; return up.id === ((_b = (_a = grpcService.selectedProject) === null || _a === void 0 ? void 0 : _a.getId) === null || _b === void 0 ? void 0 : _b.call(_a)); })) !== null && _a !== void 0 ? _a : null)
                    : null }), !isPolygonDrawing && !isDemUploading && (_jsx(ProjectDataProvider, { projects: grpcService.projects, selectedProject: grpcService.selectedProject, folders: grpcService.folders, artifacts: grpcService.artifacts, folderTree: grpcService.folderTree, loading: grpcService.isLoadingProjectStructure, onSelectProject: grpcService.selectProject, children: _jsxs(ArtifactActionsProvider, { onLoadArtifact: grpcService.loadArtifact, onToggleArtifact: grpcService.toggleArtifact, onUpdateArtifactOpacity: grpcService.updateArtifactOpacity, onUpdateArtifactWeight: grpcService.updateArtifactWeight, onUpdateArtifactMonochrome: grpcService.updateArtifactMonochrome, onHideAllArtifacts: grpcService.hideAllArtifacts, onAddStatus: grpcService.addStatus, children: [_jsx(ControlPanel, { loadedArtifacts: grpcService.loadedArtifacts, artifactControls: grpcService.artifactControls, currentBasemap: grpcService.currentBasemap, onChangeBasemap: grpcService.changeBasemap, viewer: viewer, isArtifactVisible: grpcService.isArtifactVisible, isArtifactLoading: grpcService.isArtifactLoading, getArtifactTypeName: grpcService.getArtifactTypeName, getArtifactTypeColor: grpcService.getArtifactTypeColor, onOpenAccountModal: () => modalActions.openAccount(), onOpenLoginModal: () => modalActions.openLogin(), onOpenCreateProjectModal: () => modalActions.openCreateProject(), onOpenAccountInfoModal: (tab) => modalActions.openAccountInfo(tab), onEditProject: handleEditProject, onRemoveProject: handleRemoveProject, onOpenSettings: handleOpenSettings, 
                            // Markup tools
                            activeTool: activeTool, onSetTool: onSetTool, 
                            // Unsaved items
                            unsavedItems: unsavedItems, onSaveItem: onSaveItem, onDeleteItem: onDeleteItem, onToggleUnsavedVisibility: onToggleUnsavedVisibility, isUnsavedItemVisible: isUnsavedItemVisible, 
                            // Scopes
                            scopes: scopes, scopesLoading: scopesLoading, activeScopeId: activeScopeId, onCaptureScope: onCaptureScope, onApplyScope: onApplyScope, onClearScope: onClearScope, onDeleteScope: onDeleteScope, onOverrideScope: onOverrideScope, onRenameScope: onRenameScope, onDuplicateScope: onDuplicateScope, onReorderScopes: onReorderScopes, 
                            // User folders
                            userFolders: userFolders, 
                            // Project bounds toggle
                            boundsVisible: boundsVisible, onToggleBoundsVisible: onToggleBoundsVisible, onShowInfo: onShowInfo, onFlyToArtifact: onFlyToArtifact, onEditArtifact: onEditArtifact, onDuplicateArtifact: onDuplicateArtifact, onMoveArtifact: onMoveArtifact }), _jsx(DownloadModal, { isOpen: modalState.activeModal === 'download', url: modalState.readyDownloadUrl, downloadId: modalState.readyDownloadId, onClose: modalActions.closeDownload })] }) })), _jsx(ReportModal, { isOpen: modalState.activeModal === 'report', onClose: modalActions.closeModal, statistics: modalState.reportStatistics, fallbackPropertyBounds: modalState.fallbackPropertyBounds || undefined }), _jsx(EditProjectModal, { isOpen: modalState.activeModal === 'editProject', project: modalState.selectedProject, onClose: modalActions.closeModal, onSave: handleSaveProject }), _jsx(RemoveProjectConfirmModal, { isOpen: modalState.activeModal === 'removeProject', project: modalState.selectedProject, onClose: modalActions.closeModal, onConfirm: handleConfirmRemoveProject }), _jsx(ProjectSettingsModal, { isOpen: modalState.activeModal === 'settings', project: modalState.selectedProject, onClose: modalActions.closeModal, onSave: handleSaveProject, onProjectUpdated: () => {
                    refetchUserProjects();
                } })] }));
};
const MapViewerComponent = () => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13, _14, _15, _16, _17, _18, _19, _20, _21, _22, _23, _24, _25, _26, _27, _28, _29, _30, _31, _32, _33, _34, _35, _36, _37, _38, _39, _40, _41, _42, _43, _44, _45, _46, _47, _48, _49, _50, _51, _52, _53, _54, _55, _56;
    const { isAuthenticated, isLoading, logout, user } = useAuth();
    const { refetch: refetchUserProjects, projects: userProjects } = useUserProjectsContext();
    const { state: controlPanelState } = useControlPanel();
    const [viewer, setViewer] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [hoveredPlacemark, setHoveredPlacemark] = useState(null);
    // Consolidated modal state management
    const [modalState, modalActions] = useMapViewerModals();
    const [accountInitialData, setAccountInitialData] = useState(null);
    // DEM upload flow (globe spinning, camera save/restore, back-to-edit)
    const { isDemUploading, initialDemData, setInitialDemData } = useDemUploadFlow(viewer, modalActions);
    // Markup mode hook - manages all markup tools
    const markupMode = useMarkupMode({
        onActivate: () => {
            // Only collapse control panel on mobile (< 768px)
            if (window.innerWidth < 768) {
                eventBus.emit('controlPanel:collapse');
            }
        },
    });
    // Derived tool active states from markup mode
    const isMeasureToolActive = markupMode.activeTool === 'measure';
    const isPathsToolActive = markupMode.activeTool === 'path';
    const isPlacemarkToolActive = markupMode.activeTool === 'placemark';
    const isPolygonToolActive = markupMode.activeTool === 'polygon';
    const isContourToolActive = markupMode.activeTool === 'contour';
    const isStreamToolActive = markupMode.activeTool === 'stream';
    const isLabelToolActive = markupMode.activeTool === 'label';
    const isCommentToolActive = markupMode.activeTool === 'comment';
    const isSketchToolActive = markupMode.activeTool === 'sketch';
    // Highlight mode — when enabled from the control panel, hovering or
    // clicking a contour/stream entity on the map triggers the highlight
    // flow without needing to arm a tool.
    const [contourHighlightModeEnabled, setContourHighlightModeEnabled] = useState(false);
    const [streamHighlightModeEnabled, setStreamHighlightModeEnabled] = useState(false);
    // Buffer config (shared by stream and path tools)
    const buffer = useBufferConfig();
    const { bufferEnabled, bufferDistance, bufferUnit, bufferError, bufferConfig } = buffer;
    const [hoveredDraggable, setHoveredDraggable] = useState(false);
    const [hoveredPolygonBody, setHoveredPolygonBody] = useState(false);
    const viewerRef = useRef(null);
    const hoveredPlacemarkEntityRef = useRef(null);
    const grpcService = useGrpcService(viewer);
    // Contour highlight hook for click-to-highlight functionality
    const contourHighlight = useContourHighlight({
        viewer,
        loadedArtifacts: grpcService.loadedArtifacts,
        isArtifactVisible: grpcService.isArtifactVisible,
        constraintPolygon: null,
        isActive: contourHighlightModeEnabled
    });
    // Stream highlight hook for click-to-highlight functionality on stream vectors
    const streamHighlight = useStreamHighlight({
        viewer,
        loadedArtifacts: grpcService.loadedArtifacts,
        isArtifactVisible: grpcService.isArtifactVisible,
        constraintPolygon: null,
        projectId: ((_b = (_a = grpcService.selectedProject) === null || _a === void 0 ? void 0 : _a.getId) === null || _b === void 0 ? void 0 : _b.call(_a)) || '',
        isActive: streamHighlightModeEnabled
    });
    // Centralized unit preferences
    const unitPreferences = useUnitPreferences();
    // Per-analytical-layer readiness for the selected project, flattened from the
    // folder tree's layer statuses (only not-ready layers are present). Drives
    // the colour-by gating: not-ready layers are greyed in the dropdowns and
    // excluded from the draw-time sample fetch (so a still-writing COG is never
    // opened — that would poison the per-project sampler cache until reload).
    const analyticalLayerState = useMemo(() => {
        var _a;
        const out = {};
        const visit = (nodes) => {
            var _a, _b;
            for (const node of nodes) {
                for (const ls of ((_a = node.layerStatuses) !== null && _a !== void 0 ? _a : [])) {
                    out[ls.layerName] = ls.state;
                }
                if ((_b = node.children) === null || _b === void 0 ? void 0 : _b.length)
                    visit(node.children);
            }
        };
        visit((_a = grpcService.folderTree) !== null && _a !== void 0 ? _a : []);
        return out;
    }, [grpcService.folderTree]);
    // Ready subset of the sampling layers; reference-stable per content so the
    // tools' refetch-on-ready effects only fire when readiness actually changes.
    const readyLayers = useMemo(() => readyAnalyticalLayers(analyticalLayerState), [analyticalLayerState]);
    // Publish to the readiness store the colour-by dropdowns subscribe to (they
    // render outside the ProjectDataProvider subtree).
    useEffect(() => {
        setLayerReadiness(analyticalLayerState);
    }, [analyticalLayerState]);
    // Measure tool hook for distance measurements
    const measureTool = useMeasure({
        viewer,
        isActive: isMeasureToolActive,
        projectId: ((_d = (_c = grpcService.selectedProject) === null || _c === void 0 ? void 0 : _c.getId) === null || _d === void 0 ? void 0 : _d.call(_c)) || null,
        constraintPolygon: null,
        defaultDistanceUnit: unitPreferences.distanceUnit,
        defaultAreaUnit: unitPreferences.areaUnit,
        readyLayers,
    });
    // Tracks the path/polygon id currently in edit mode so the tools can scope clicks to it.
    // Synced from serverArtifactEdit.editingState in useEffects further down (after both hooks exist).
    const [editingPathId, setEditingPathId] = useState(null);
    const [editingPolygonId, setEditingPolygonId] = useState(null);
    // True while a saved path/polygon is open in read-only VIEW mode — the tool
    // locks geometry editing so the user can't make changes they can't save.
    const [pathViewLocked, setPathViewLocked] = useState(false);
    const [polygonViewLocked, setPolygonViewLocked] = useState(false);
    // Server artifacts whose render is suppressed because an editable copy is on
    // screen (view/edit). Tracked separately from visibility so the layer-tree
    // entry stays active while the copy is shown.
    const [suppressedArtifactIds, setSuppressedArtifactIds] = useState(new Set());
    // Paths tool hook for multi-point path drawing
    const pathsTool = usePaths({
        viewer,
        isActive: isPathsToolActive,
        projectId: ((_f = (_e = grpcService.selectedProject) === null || _e === void 0 ? void 0 : _e.getId) === null || _f === void 0 ? void 0 : _f.call(_e)) || null,
        constraintPolygon: null,
        editingPathId,
        viewLocked: pathViewLocked,
        readyLayers
    });
    // The in-progress path carries TWO independent display units: one for the
    // per-segment distance labels, one for the total length — so you can plan
    // segments in metres while reading the total in km. Both seed from (and reset
    // to) the global metric/imperial preference; the HUD pickers override each
    // locally without touching the global pref.
    const [pathSegmentUnit, setPathSegmentUnit] = useState(unitPreferences.distanceUnit);
    const [pathTotalUnit, setPathTotalUnit] = useState(unitPreferences.distanceUnit);
    useEffect(() => {
        setPathSegmentUnit(unitPreferences.distanceUnit);
        setPathTotalUnit(unitPreferences.distanceUnit);
    }, [unitPreferences.distanceUnit]);
    // The segment-label unit drives the on-map labels (usePaths owns those).
    useEffect(() => {
        pathsTool.setDefaultUnit(pathSegmentUnit);
    }, [pathSegmentUnit, pathsTool.setDefaultUnit]);
    // Polygons tool hook for drawing area polygons
    // Initialized before placemarks so placemarks render on top
    const polygonTool = usePolygons({
        viewer,
        isActive: isPolygonToolActive,
        projectId: ((_h = (_g = grpcService.selectedProject) === null || _g === void 0 ? void 0 : _g.getId) === null || _h === void 0 ? void 0 : _h.call(_g)) || null,
        constraintPolygon: null,
        editingPolygonId,
        viewLocked: polygonViewLocked
    });
    // Placemarks tool hook for placing markers on map
    // Initialized last among markup tools so placemarks render on top
    const placemarksTool = usePlacemarks({
        viewer,
        isActive: isPlacemarkToolActive,
        projectId: ((_k = (_j = grpcService.selectedProject) === null || _j === void 0 ? void 0 : _j.getId) === null || _k === void 0 ? void 0 : _k.call(_j)) || null,
        constraintPolygon: null
    });
    // Sketch tool — freehand pen / eraser. Strokes live in-memory until Finish → save.
    const [sketchMode, setSketchMode] = useState('pen');
    const [sketchColor, setSketchColor] = useState(DEFAULT_COLOR);
    const [sketchWidth, setSketchWidth] = useState(4);
    // Pen opacity, a property of the ink itself. The canvas-level opacity
    // sliders multiply over this rather than replacing it.
    const [sketchOpacity, setSketchOpacity] = useState(1);
    const [sketchEraserSize, setSketchEraserSize] = useState(24);
    const [sketchSaveOpen, setSketchSaveOpen] = useState(false);
    const sketchTool = useSketch({
        viewer,
        isActive: isSketchToolActive,
        projectId: ((_m = (_l = grpcService.selectedProject) === null || _l === void 0 ? void 0 : _l.getId) === null || _m === void 0 ? void 0 : _m.call(_l)) || null,
        mode: sketchMode,
        color: sketchColor,
        width: sketchWidth,
        opacity: sketchOpacity,
        eraserSize: sketchEraserSize
    });
    // `defaultLabelStyle` is what the pop-out drives when no label is
    // selected; useLabels reads it on placement so the next-placed label
    // inherits the user's current selections. The `useLabels` call itself
    // is hoisted further down — past useServerArtifactRenderer — so it can
    // accept the saved-labels Map (needed for click/double-click hydration).
    const [defaultLabelStyle, setDefaultLabelStyle] = useState(DEFAULT_LABEL_STYLE);
    // Live mirror of whatever the user is currently typing in the label
    // inline editor. Lifted here so the surrounding LabelStylePopout's
    // Save section can validate against the live text (and persist it)
    // before Enter is pressed.
    const [labelEditorText, setLabelEditorText] = useState('');
    // Ref to the live LabelInlineEditor so Save / Save-later can
    // imperatively commit whatever's typed before proceeding.
    const labelInlineEditorRef = useRef(null);
    // Placemark clustering is disabled: the numbered badge it drew over
    // overlapping placemarks went stale when a placemark was dragged apart
    // (clusters only recompute on camera move / count change), and the badge
    // itself was unwanted. Overlapping placemarks now simply render on top of
    // each other; the spider picker never activates.
    const placemarkClustering = usePlacemarkClustering({
        viewer,
        placemarks: placemarksTool.placemarks,
        clusterThresholdPx: 40,
        enabled: false,
        isItemVisible: placemarksTool.isItemVisible
    });
    // User folders hook for server-side folder persistence
    const userFolders = useUserFolders({
        projectId: ((_p = (_o = grpcService.selectedProject) === null || _o === void 0 ? void 0 : _o.getId) === null || _p === void 0 ? void 0 : _p.call(_o)) || null,
        isAuthenticated
    });
    // Distinct custom colours already used across this project's artifacts (not
    // the default swatches) — surfaced in the Sketch picker so a colour used
    // elsewhere ("that orange for roads") is one click away.
    const projectColors = useMemo(() => {
        const defaults = new Set(TOOL_COLORS.map(c => c.color.toUpperCase()));
        const seen = new Set();
        const out = [];
        for (const folder of userFolders.folders) {
            for (const a of folder.artifacts) {
                const c = (a.color || '').toUpperCase();
                if (/^#[0-9A-F]{6}$/.test(c) && !defaults.has(c) && !seen.has(c)) {
                    seen.add(c);
                    out.push(c);
                }
            }
        }
        return out.slice(0, 12);
    }, [userFolders.folders]);
    // Persist the current sketch canvas as one 'sketch' artifact, then clear it
    // and close the tool so the saved version renders from the folder tree.
    // Declared after userFolders so the dependency array isn't in its TDZ.
    // The canvas and the modal are only torn down once the write lands. A
    // rejection travels back to the save form (the Finish modal's save section
    // or the Unsaved Drawings row), which keeps the strokes on screen and shows
    // the server's reason, so a refused save never looks like a completed one.
    const handleSaveSketch = useCallback(async (name, folderId) => {
        const data = sketchTool.serializeForSave();
        if (!data)
            throw new Error('Draw at least one stroke before saving.');
        await userFolders.createArtifact({
            folderId,
            artifactType: 'sketch',
            name: name || 'Sketch',
            color: data.color,
            geometryJson: data.geometryJson,
        });
        await userFolders.fetchFolders();
        setSketchSaveOpen(false);
        sketchTool.resetCanvas();
        markupMode.setTool(null);
    }, [sketchTool, userFolders, markupMode]);
    const [boundsVisible, setBoundsVisible] = useState(true);
    // Open-state for the per-tool markup upload modal. Set by the toolbar's
    // Upload pill (CanvasBottomChrome), cleared by the modal itself on
    // close / successful upload.
    const [markupUploadTool, setMarkupUploadTool] = useState(null);
    // Print View mode (entered from the bottom toolbar) — see PRINT_VIEW_PLAN.md.
    const [printOpen, setPrintOpen] = useState(false);
    // Print is a paid-project feature: anything short of full access routes to
    // the unlock modal instead, and a demo-list preview (not the user's project)
    // gets the account restriction modal — same gate the Download button uses.
    const [printRestricted, setPrintRestricted] = useState(false);
    const handlePrintRequest = useCallback(() => {
        var _a, _b;
        const pid = (_b = (_a = grpcService.selectedProject) === null || _a === void 0 ? void 0 : _a.getId) === null || _b === void 0 ? void 0 : _b.call(_a);
        if (!pid) {
            setPrintOpen(true);
            return;
        }
        const ownProject = userProjects.find((p) => p.id === pid);
        if (!ownProject) {
            setPrintRestricted(true);
            return;
        }
        // Only full access prints; every other state routes to the unlock flow.
        // That includes still-processing (accessLevel 'none' while the demo window
        // is masked by the lifecycle, yet layers already render for the owner) —
        // the print export is pure client-side canvas readback, so this predicate
        // is the only gate. 'failed' is carved out via the raw lifecycle field:
        // a total pipeline failure produces nothing printable and prompting
        // payment there would be nonsense.
        if (!hasFullAccess(ownProject.accessLevel) && ownProject.processingStatus !== 'failed') {
            const demoActive = ownProject.demoExpiresAt != null &&
                Date.now() / 1000 < ownProject.demoExpiresAt;
            eventBus.emit('project:openUnlockModal', {
                projectId: ownProject.id,
                projectName: ownProject.name,
                demoExpiresAt: ownProject.demoExpiresAt,
                action: 'print',
                demoActive,
            });
            return;
        }
        setPrintOpen(true);
    }, [grpcService.selectedProject, userProjects]);
    useEffect(() => {
        setBoundsVisible(true);
    }, [(_r = (_q = grpcService.selectedProject) === null || _q === void 0 ? void 0 : _q.getId) === null || _r === void 0 ? void 0 : _r.call(_q)]);
    const projectBounds = useProjectBoundsWithViewMode(viewer, grpcService.selectedProject, 'project-structure', boundsVisible);
    // Forward-declared ref for serverArtifactEdit.startView (created below).
    // Lets the renderer hook dispatch saved-path clicks into PathOptionsDialog (view mode).
    const startViewRef = useRef(null);
    // Last-known placement (drag position, resized width, segment view) of each
    // tool-rail path popout, keyed by pathId:segment. Read during the view-mode
    // close handover so the migrated server-rail popout re-mounts where the user
    // left it instead of snapping back to its spawn position.
    const pathPopoutLayoutsRef = useRef(new Map());
    // Render server artifacts (My Layers) on the map
    // Server artifacts visibility is controlled by individual artifact settings, not markup mode
    const serverArtifactRenderer = useServerArtifactRenderer({
        viewer,
        folders: userFolders.folders,
        artifactVisibility: userFolders.artifactVisibility,
        artifactOpacity: userFolders.artifactOpacity,
        folderVisibility: userFolders.folderVisibility,
        derivedVisibility: userFolders.derivedVisibility,
        suppressedArtifactIds,
        // Saved-path / saved-polygon / saved-stream clicks bypass the generic
        // ServerArtifactDialog and load into the live tool, opening their own dialog
        // in view mode (full controls incl. the measurements toggle).
        onPathArtifactClick: (artifact) => { var _a; return (_a = startViewRef.current) === null || _a === void 0 ? void 0 : _a.call(startViewRef, artifact); },
        onPolygonArtifactClick: (artifact) => { var _a; return (_a = startViewRef.current) === null || _a === void 0 ? void 0 : _a.call(startViewRef, artifact); },
        onStreamArtifactClick: (artifact) => { var _a; return (_a = startViewRef.current) === null || _a === void 0 ? void 0 : _a.call(startViewRef, artifact); }
    });
    // Map saved-label artifactId -> the folder it lives in. The label
    // tool needs this so that when a user double-clicks a saved label and
    // edits it, the eventual updateArtifact knows which folder to target.
    const resolveServerFolderId = useCallback((artifactId) => {
        for (const folder of userFolders.folders) {
            for (const artifact of folder.artifacts) {
                if (artifact.id === artifactId)
                    return folder.id;
            }
        }
        return undefined;
    }, [userFolders.folders]);
    // Role of the signed-in user on the open project. Drives which controls the
    // saved-artifact surfaces render: a client viewer may only change what they
    // drew themselves.
    const selectedProjectCaps = useMemo(() => capabilitiesForProject(userProjects.find(p => { var _a, _b; return p.id === (((_b = (_a = grpcService.selectedProject) === null || _a === void 0 ? void 0 : _a.getId) === null || _b === void 0 ? void 0 : _b.call(_a)) || null); })), [userProjects, grpcService.selectedProject]);
    // A saved label opens by being pulled into the editable Map, which is itself
    // the edit surface, so authorship is checked before the pull rather than by
    // hiding buttons afterwards. Someone else's label stays inert for a viewer.
    const canEditSavedLabel = useCallback((artifactId) => {
        for (const folder of userFolders.folders) {
            const artifact = folder.artifacts.find(a => a.id === artifactId);
            if (artifact)
                return canModifyArtifact(artifact, user === null || user === void 0 ? void 0 : user.userId, selectedProjectCaps.isClientViewer);
        }
        return canModifyArtifact(undefined, user === null || user === void 0 ? void 0 : user.userId, selectedProjectCaps.isClientViewer);
    }, [userFolders.folders, user === null || user === void 0 ? void 0 : user.userId, selectedProjectCaps.isClientViewer]);
    // Labels tool — Figma-style: click to place, type to label, double-
    // click to re-edit inline. Saved labels are pulled in from
    // serverArtifactRenderer.savedLabels and hydrated into the editable
    // Map on click/double-click.
    const labelsTool = useLabels({
        viewer,
        isActive: isLabelToolActive,
        projectId: ((_t = (_s = grpcService.selectedProject) === null || _s === void 0 ? void 0 : _s.getId) === null || _t === void 0 ? void 0 : _t.call(_s)) || null,
        constraintPolygon: null,
        defaultStyle: defaultLabelStyle,
        savedLabels: serverArtifactRenderer.savedLabels,
        resolveServerFolderId,
        canEditSavedLabel,
        // Disarm the Label toolbar tool as soon as the first label's text
        // is committed. The pop-out keeps showing because `selectedLabel`
        // is now set (see the relaxed render gate below), so the user can
        // still style and save without a stray map click dropping another
        // empty label underneath them.
        onFirstCommit: () => markupMode.setTool(null)
    });
    // Hide a saved label's invisible picker entity while its hydrated
    // copy is open in the label tool. Otherwise scene.pick can match
    // either entity at the same position and routing becomes flaky.
    const hiddenSavedLabelIds = useMemo(() => {
        const ids = new Set();
        labelsTool.labels.forEach(l => { if (l.serverArtifactId)
            ids.add(l.serverArtifactId); });
        return ids;
    }, [labelsTool.labels]);
    useEffect(() => {
        serverArtifactRenderer.savedLabels.forEach((label, id) => {
            if (!label.entity)
                return;
            label.entity.show = !hiddenSavedLabelIds.has(id);
        });
    }, [hiddenSavedLabelIds, serverArtifactRenderer.savedLabels]);
    const serverArtifactEdit = useServerArtifactEdit({
        tools: {
            placemarksTool: {
                addPlacemark: placemarksTool.addPlacemark,
                selectPlacemarkById: placemarksTool.selectPlacemarkById,
            },
            measureTool: {
                addMeasurementFromPoints: measureTool.addMeasurementFromPoints,
                selectMeasurementById: measureTool.selectMeasurementById,
                addCircleFromPoints: measureTool.addCircleFromPoints,
                selectCircleById: measureTool.selectCircleById,
            },
            pathsTool: {
                createPathFromCoordinates: pathsTool.createPathFromCoordinates,
                selectPathById: pathsTool.selectPathById,
                restoreBuffer: pathsTool.restoreBuffer,
            },
            polygonTool: {
                createPolygonFromCoordinates: polygonTool.createPolygonFromCoordinates,
                selectPolygonById: polygonTool.selectPolygonById,
            },
            streamHighlight: {
                startEditFromArtifact: streamHighlight.startEditFromArtifact,
                promoteToEdit: streamHighlight.promoteToEdit,
            },
            contourHighlight: {
                startEditFromArtifact: contourHighlight.startEditFromArtifact,
            },
            sketchTool: {
                loadStrokes: sketchTool.loadStrokes,
            },
        },
        setTool: markupMode.setTool,
        dismissServerSelection: serverArtifactRenderer.dismissSelection,
        hideServerArtifact: (id) => setSuppressedArtifactIds(prev => {
            if (prev.has(id))
                return prev;
            const next = new Set(prev);
            next.add(id);
            return next;
        }),
        showServerArtifact: (id) => setSuppressedArtifactIds(prev => {
            if (!prev.has(id))
                return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
        }),
    });
    // Hook the forward-declared startView ref now that serverArtifactEdit exists.
    useEffect(() => {
        startViewRef.current = serverArtifactEdit.startView;
    }, [serverArtifactEdit.startView]);
    // Sketch edit (right-click → Edit): the saved canvas is loaded into the live
    // tool by serverArtifactEdit.startEdit('sketch'). Save writes the modified
    // strokes back to the same artifact (updateArtifact); Cancel discards. Both
    // un-hide the saved version (cancelEdit) and clear the local canvas.
    const isEditingSketch = ((_u = serverArtifactEdit.editingState) === null || _u === void 0 ? void 0 : _u.artifactType) === 'sketch';
    // Name of the sketch currently being edited (for the "Save changes to …" label).
    const editingSketchName = useMemo(() => {
        const s = serverArtifactEdit.editingState;
        if (!s || s.artifactType !== 'sketch')
            return '';
        for (const f of userFolders.folders) {
            const a = f.artifacts.find(x => x.id === s.serverArtifactId);
            if (a)
                return a.name;
        }
        return '';
    }, [serverArtifactEdit.editingState, userFolders.folders]);
    const handleSaveSketchEdit = useCallback(async () => {
        const s = serverArtifactEdit.editingState;
        if (!s || s.artifactType !== 'sketch')
            return;
        const data = sketchTool.serializeForSave();
        if (!data)
            throw new Error('Draw at least one stroke before saving.');
        // Preserve the saved artifact's name; colour follows the edited strokes.
        let name = 'Sketch';
        for (const f of userFolders.folders) {
            const a = f.artifacts.find(x => x.id === s.serverArtifactId);
            if (a) {
                name = a.name;
                break;
            }
        }
        // Same contract as a fresh save: the edit session only ends on success, and
        // a refusal propagates to the sketch panel, which shows it under the
        // buttons with the edited strokes still on the canvas.
        await userFolderService.updateArtifact({
            artifactId: s.serverArtifactId,
            name,
            color: data.color,
            geometryJson: data.geometryJson,
        });
        await userFolders.fetchFolders();
        serverArtifactEdit.cancelEdit();
        sketchTool.resetCanvas();
        markupMode.setTool(null);
    }, [serverArtifactEdit, sketchTool, userFolders, markupMode]);
    const handleCancelSketchEdit = useCallback(() => {
        serverArtifactEdit.cancelEdit();
        sketchTool.resetCanvas();
        markupMode.setTool(null);
    }, [serverArtifactEdit, sketchTool, markupMode]);
    // Bridge serverArtifactEdit's editingState back to pathsTool / polygonTool so they can scope clicks
    // to the artifact being edited (prevents new pending paths/polygons during edit).
    useEffect(() => {
        var _a, _b;
        const e = serverArtifactEdit.editingState;
        setEditingPathId((e === null || e === void 0 ? void 0 : e.artifactType) === 'path' ? ((_a = e.unsavedItemId) !== null && _a !== void 0 ? _a : null) : null);
        setEditingPolygonId((e === null || e === void 0 ? void 0 : e.artifactType) === 'polygon' ? ((_b = e.unsavedItemId) !== null && _b !== void 0 ? _b : null) : null);
    }, [serverArtifactEdit.editingState]);
    // Bridge view-mode so the tools lock geometry editing for a saved artifact
    // opened read-only (it's loaded just so the dialog + measurements work).
    useEffect(() => {
        const v = serverArtifactEdit.viewingState;
        setPathViewLocked((v === null || v === void 0 ? void 0 : v.artifactType) === 'path');
        setPolygonViewLocked((v === null || v === void 0 ? void 0 : v.artifactType) === 'polygon');
    }, [serverArtifactEdit.viewingState]);
    useEventSubscribe('artifact:flyTo', (payload) => {
        const { artifactId } = payload;
        userFolders.setArtifactVisibility(artifactId, true);
        const folder = userFolders.folders.find(f => f.artifacts.some(a => a.id === artifactId));
        if (folder && userFolders.folderVisibility[folder.id] === false) {
            userFolders.toggleFolderVisibility(folder.id);
        }
        setTimeout(() => {
            serverArtifactRenderer.flyToArtifact(artifactId);
        }, 50);
    }, [serverArtifactRenderer, userFolders]);
    // Last step of the create flow: every AOI-gathering path ends by asking for
    // this confirmation, which owns the create RPC.
    useEventSubscribe('project:confirmCreate', (payload) => {
        modalActions.openConfirmCreate(payload.projectName);
    }, [modalActions]);
    // Compute whether contour/stream layers are visible (for enabling/disabling markup tools)
    const { hasContourLayers, hasStreamLayers, contourLayers, streamLayers } = useLayerTypeDetection(grpcService.loadedArtifacts, grpcService.isArtifactVisible, grpcService.artifacts);
    const profileCrosshair = useProfileCrosshair({ viewer });
    const handleProfileHoverPosition = useCallback((position, color) => {
        if (position) {
            profileCrosshair.showCrosshair(position, color);
        }
        else {
            profileCrosshair.hideCrosshair();
        }
    }, [profileCrosshair]);
    const projectId = ((_w = (_v = grpcService.selectedProject) === null || _v === void 0 ? void 0 : _v.getId) === null || _w === void 0 ? void 0 : _w.call(_v)) || null;
    // True only when genuinely inside a project: a project is selected AND the
    // control panel is in project-structure view. The global view and the
    // project-creation flow are both `false`. Gates markup/artifact interaction
    // so other projects' elements can't be clicked, dragged, or hovered once
    // you've left the project (the back arrow flips viewMode but keeps the
    // project selected, so projectId alone isn't enough to tell).
    const inProject = !!projectId && controlPanelState.viewMode === 'project-structure';
    // Disarm any active markup tool the moment you leave a project. Otherwise
    // the tool stays armed and the map keeps accepting clicks even though the
    // toolbar is hidden — letting you "draw" on a project you've left.
    useEffect(() => {
        if (!inProject && markupMode.activeTool) {
            markupMode.setTool(null);
        }
    }, [inProject, markupMode.activeTool, markupMode.setTool]);
    // Entering print mode disarms any active markup/drawing tool. Print is a
    // read-only takeover, but the drawing tools run their own Cesium event
    // handlers (gated on isActive, NOT the event-router's interactionEnabled), so
    // an armed path/polygon tool would otherwise keep drawing through the sheet.
    useEffect(() => {
        if (printOpen && markupMode.activeTool) {
            markupMode.setTool(null);
        }
    }, [printOpen, markupMode.activeTool, markupMode.setTool]);
    // Callbacks for capturing/restoring layer and artifact configs in scopes
    const getLayerConfigs = useCallback(() => {
        const configs = [];
        grpcService.loadedArtifacts.forEach((_data, artifactId) => {
            var _a;
            const controls = grpcService.artifactControls.get(artifactId);
            configs.push({
                artifactId,
                visible: grpcService.isArtifactVisible(artifactId),
                opacity: (_a = controls === null || controls === void 0 ? void 0 : controls.opacity) !== null && _a !== void 0 ? _a : 1.0,
                weight: controls === null || controls === void 0 ? void 0 : controls.weight,
            });
        });
        return configs;
    }, [grpcService]);
    const getArtifactConfigs = useCallback(() => {
        const configs = [];
        let sortOrder = 0;
        // Capture user artifacts from user folders
        userFolders.folders.forEach(folder => {
            folder.artifacts.forEach(artifact => {
                var _a, _b;
                configs.push({
                    artifactType: 'user',
                    artifactId: artifact.id,
                    visible: (_a = userFolders.artifactVisibility[artifact.id]) !== null && _a !== void 0 ? _a : true,
                    opacity: (_b = userFolders.artifactOpacity[artifact.id]) !== null && _b !== void 0 ? _b : 1.0,
                    colorOverride: artifact.color,
                    sortOrder: sortOrder++,
                });
            });
        });
        // Capture system artifacts (project artifacts loaded via gRPC). Opacity is a
        // first-class field; weight + monochrome styling ride in the free-form
        // viewState JSON, which round-trips through the scope backend without a
        // proto/DB change (the proto LayerConfig has no weight field).
        grpcService.loadedArtifacts.forEach((_data, artifactId) => {
            var _a;
            const visible = grpcService.isArtifactVisible(artifactId);
            const controls = grpcService.artifactControls.get(artifactId);
            const styling = {};
            if (controls === null || controls === void 0 ? void 0 : controls.monochrome)
                styling.monochrome = controls.monochrome;
            if (typeof (controls === null || controls === void 0 ? void 0 : controls.weight) === 'number')
                styling.weight = controls.weight;
            configs.push({
                artifactType: 'system',
                artifactId,
                visible,
                opacity: (_a = controls === null || controls === void 0 ? void 0 : controls.opacity) !== null && _a !== void 0 ? _a : 1.0,
                viewState: Object.keys(styling).length ? JSON.stringify(styling) : undefined,
                sortOrder: sortOrder++,
            });
        });
        return configs;
    }, [userFolders, grpcService]);
    const applyLayerConfigs = useCallback((configs) => {
        var _a;
        const scopeArtifactIds = new Set(configs.filter(c => c.visible).map(c => c.artifactId));
        // Visually hide all loaded artifacts not in the scope (without unloading from memory)
        grpcService.loadedArtifacts.forEach((artifact, artifactId) => {
            if (!scopeArtifactIds.has(artifactId) && grpcService.isArtifactVisible(artifactId)) {
                if (artifact.cesiumObject)
                    artifact.cesiumObject.show = false;
                grpcService.artifactActions.updateVisibility(artifactId, false);
            }
        });
        // Show/load only what the scope specifies as visible
        for (const config of configs) {
            if (config.visible) {
                const isLoaded = grpcService.loadedArtifacts.has(config.artifactId);
                if (isLoaded) {
                    const artifact = grpcService.loadedArtifacts.get(config.artifactId);
                    if (artifact === null || artifact === void 0 ? void 0 : artifact.cesiumObject)
                        artifact.cesiumObject.show = true;
                    grpcService.artifactActions.updateVisibility(config.artifactId, true);
                }
                else {
                    const artifactMetadata = (_a = grpcService.artifacts) === null || _a === void 0 ? void 0 : _a.find((a) => a.getId() === config.artifactId);
                    if (artifactMetadata) {
                        grpcService.loadArtifact(config.artifactId, artifactMetadata);
                    }
                }
            }
            grpcService.updateArtifactOpacity(config.artifactId, config.opacity);
        }
    }, [grpcService]);
    const applyArtifactConfigs = useCallback((configs) => {
        const userConfigs = configs
            .filter(c => c.artifactType === 'user')
            .map(c => ({ artifactId: c.artifactId, visible: c.visible, opacity: c.opacity }));
        userFolders.restoreUserArtifactVisibility(userConfigs);
        // Restore opacity + weight + monochrome for system artifacts in a single
        // patch-merge per artifact (one write, so the three can't clobber each other).
        // Opacity comes from the first-class field; weight + monochrome from the
        // viewState JSON. monochrome is always set (value or undefined) so switching
        // to a scope without it turns monochrome back off.
        for (const c of configs) {
            if (c.artifactType !== 'system')
                continue;
            let monochrome;
            let weight;
            if (c.viewState) {
                try {
                    const styling = JSON.parse(c.viewState);
                    monochrome = styling === null || styling === void 0 ? void 0 : styling.monochrome;
                    if (typeof (styling === null || styling === void 0 ? void 0 : styling.weight) === 'number')
                        weight = styling.weight;
                }
                catch (_a) {
                    // leave monochrome/weight undefined on parse failure
                }
            }
            const patch = { opacity: c.opacity, monochrome };
            if (weight !== undefined)
                patch.weight = weight;
            grpcService.artifactActions.patchControls(c.artifactId, patch);
        }
    }, [userFolders, grpcService]);
    const scopesHook = useScopes({
        projectId,
        viewer,
        getLayerConfigs,
        getArtifactConfigs,
        applyLayerConfigs,
        applyArtifactConfigs,
    });
    const polygonStats = usePolygonStats(projectId);
    const serverArtifactPolygonStats = usePolygonStats(projectId);
    const serverArtifactView = useServerArtifactView(((_x = serverArtifactRenderer.selectedArtifact) === null || _x === void 0 ? void 0 : _x.artifact) || null, ((_z = (_y = grpcService.selectedProject) === null || _y === void 0 ? void 0 : _y.getId) === null || _z === void 0 ? void 0 : _z.call(_y)) || null);
    // Clear tool polygon stats when polygon geometry changes (vertex drag, insert, delete)
    const selectedToolPolygon = polygonTool.selectedPolygon
        ? polygonTool.polygons.get(polygonTool.selectedPolygon.polygonId)
        : null;
    const selectedToolPolygonFingerprint = selectedToolPolygon
        ? `${selectedToolPolygon.vertices.length}_${selectedToolPolygon.areaSquareMeters}`
        : null;
    const polygonFingerprintRef = useRef(null);
    useEffect(() => {
        if (selectedToolPolygonFingerprint === null) {
            if (polygonFingerprintRef.current !== null) {
                polygonStats.clear();
            }
            polygonFingerprintRef.current = null;
            return;
        }
        if (polygonFingerprintRef.current !== null && polygonFingerprintRef.current !== selectedToolPolygonFingerprint) {
            polygonStats.clear();
        }
        polygonFingerprintRef.current = selectedToolPolygonFingerprint;
    }, [selectedToolPolygonFingerprint]);
    // Clear server artifact polygon stats when selected artifact changes
    useEffect(() => {
        serverArtifactPolygonStats.clear();
    }, [(_1 = (_0 = serverArtifactRenderer.selectedArtifact) === null || _0 === void 0 ? void 0 : _0.artifact) === null || _1 === void 0 ? void 0 : _1.id]);
    // Draft comment promotion (promotes drafts when unsaved artifacts are saved)
    useDraftCommentPromotion(projectId);
    // Comment thread system — Figma-style coord-anchored pins.
    const commentsApi = useComments({ projectId: projectId !== null && projectId !== void 0 ? projectId : '' });
    const { collaborators: projectCollaborators } = useProjectCollaborators(projectId);
    const { tags: projectCommentTags } = useProjectCommentTags(projectId);
    // Active tag filter set, shared between the modal/list and the map pins.
    // The CommentsPanel owns the toggle UI and broadcasts changes via eventBus;
    // MapViewer mirrors them here so the pin layer can re-render.
    const [activeTagFilters, setActiveTagFilters] = useState(new Set());
    useEventSubscribe('comments:tagFiltersChanged', (payload) => {
        if (payload.projectId !== projectId)
            return;
        setActiveTagFilters(new Set(payload.tags));
    }, [projectId]);
    // Visibility toggle for the entire pin overlay layer — controlled by the
    // eye icon in the CommentsSection header. Open thread popovers and any
    // active draft pill are unaffected (those are in-progress UI, not part
    // of the resting pin clutter).
    const [commentPinsVisible, setCommentPinsVisible] = useState(() => readCommentsVisible());
    useEventSubscribe('comments:visibilityChanged', (payload) => {
        setCommentPinsVisible(payload.visible);
    });
    // Fly the camera to a comment's anchor when the user clicks "Fly to"
    // in the CommentsPanel. Frame the destination ~500m above the anchor so
    // the pin sits comfortably in view.
    useEventSubscribe('comment:flyTo', (payload) => {
        var _a;
        if (!viewer || viewer.isDestroyed())
            return;
        const destination = Cesium.Cartesian3.fromDegrees(payload.longitude, payload.latitude, ((_a = payload.height) !== null && _a !== void 0 ? _a : 0) + 500);
        viewer.camera.flyTo({ destination, duration: 1.2 });
    }, [viewer]);
    const [commentDrafts, setCommentDrafts] = useState([]);
    const [commentThreads, setCommentThreads] = useState([]);
    // Shared map of live screen positions (keyed by entry id) so leader lines
    // can follow whichever surface — pill or popover — is being dragged.
    const [popoverPositions, setPopoverPositions] = useState(new Map());
    // Actual rendered size of each open thread popover, reported by the
    // popover via ResizeObserver. The leader line uses this to compute a
    // closest-point attach against the real bbox (not the max size).
    const [popoverSizes, setPopoverSizes] = useState(new Map());
    const updatePopoverPosition = useCallback((id, pos) => {
        setPopoverPositions(prev => {
            const next = new Map(prev);
            next.set(id, pos);
            return next;
        });
    }, []);
    const updatePopoverSize = useCallback((id, size) => {
        setPopoverSizes(prev => {
            const cur = prev.get(id);
            if (cur && cur.width === size.width && cur.height === size.height)
                return prev;
            const next = new Map(prev);
            next.set(id, size);
            return next;
        });
    }, []);
    const closeDraft = useCallback((id) => {
        setCommentDrafts(prev => prev.filter(p => p.id !== id));
        setPopoverPositions(prev => {
            const next = new Map(prev);
            next.delete(id);
            return next;
        });
    }, []);
    const closeThread = useCallback((id) => {
        setCommentThreads(prev => prev.filter(p => p.id !== id));
        setPopoverPositions(prev => {
            const next = new Map(prev);
            next.delete(id);
            return next;
        });
        setPopoverSizes(prev => {
            const next = new Map(prev);
            next.delete(id);
            return next;
        });
    }, []);
    // Open a thread popover for a given comment, anchored at the click point.
    const openThreadAt = useCallback((commentId, clickPos) => {
        setCommentThreads(prev => {
            if (prev.some(p => p.threadCommentId === commentId))
                return prev;
            return [...prev, {
                    id: `thread-${commentId}`,
                    screenPosition: clickPos,
                    threadCommentId: commentId,
                }];
        });
        // Disarm the comment tool — same policy as the old onSelectPin flow.
        markupMode.setTool(null);
    }, [markupMode]);
    const commentPins = useCommentPins({
        viewer,
        isToolActive: isCommentToolActive,
        onDropPin: (event) => {
            const id = `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            setCommentDrafts(prev => [...prev, {
                    id,
                    screenPosition: event.screenPosition,
                    draftAnchor: {
                        latitude: event.latitude,
                        longitude: event.longitude,
                        height: event.height,
                    },
                    preLinkedArtifactId: event.artifactIdAtClick,
                }]);
            // Disarm so the next map click doesn't drop another pin accidentally.
            markupMode.setTool(null);
        },
    });
    // Fetch comments whenever a project is selected so pins render on load.
    useEffect(() => {
        if (projectId) {
            commentsApi.fetchComments();
        }
    }, [projectId, commentsApi.fetchComments]);
    // Resolve a specific popover entry's thread Comment from the canonical
    // comments list, with deleted replies pruned. Returns undefined if the
    // root has been deleted (so an in-flight delete closes the popover).
    const resolveThreadComment = useCallback((threadCommentId) => {
        var _a;
        if (!threadCommentId)
            return undefined;
        const root = commentsApi.comments.find(c => c.id === threadCommentId);
        if (!root || root.deleted)
            return undefined;
        const pruneDeletedReplies = (cs = []) => cs.filter(c => !c.deleted).map(c => { var _a; return ({ ...c, replies: pruneDeletedReplies((_a = c.replies) !== null && _a !== void 0 ? _a : []) }); });
        return { ...root, replies: pruneDeletedReplies((_a = root.replies) !== null && _a !== void 0 ? _a : []) };
    }, [commentsApi.comments]);
    // Auto-close any open thread popovers whose root comment has been deleted
    // (could happen via the panel, another tab, etc). Drafts are unaffected.
    useEffect(() => {
        setCommentThreads(prev => prev.filter(p => {
            const c = commentsApi.comments.find(x => x.id === p.threadCommentId);
            return !!c && !c.deleted;
        }));
    }, [commentsApi.comments]);
    const measurementCrosshairFraction = useMemo(() => {
        if (!measureTool.hoveredMeasurement)
            return null;
        const m = measureTool.measurements.get(measureTool.hoveredMeasurement.measurementId);
        if (!m || m.distanceMeters === 0)
            return null;
        return Math.max(0, Math.min(1, measureTool.hoveredMeasurement.distanceFromStart / m.distanceMeters));
    }, [measureTool.hoveredMeasurement, measureTool.measurements]);
    const pathCrosshairFraction = useMemo(() => {
        if (!pathsTool.hoveredPath)
            return null;
        const p = pathsTool.paths.get(pathsTool.hoveredPath.pathId);
        if (!p || p.totalDistanceMeters === 0)
            return null;
        return Math.max(0, Math.min(1, pathsTool.hoveredPath.distanceFromStart / p.totalDistanceMeters));
    }, [pathsTool.hoveredPath, pathsTool.paths]);
    // Unsaved items facade — aggregates all tool items and dispatches operations by type
    const unsavedFacade = useUnsavedItemsFacade({
        placemarksTool, measureTool, pathsTool, polygonTool, contourHighlight, streamHighlight, labelsTool, sketchTool
    });
    // Visible user-drawn annotations for the print legend's "Features" section —
    // saved server artifacts (folders) plus unsaved tool items, filtered to what
    // is actually visible. Only built while print is open. Saved use the cascaded
    // folder+artifact visibility; unsaved use the facade predicate. Each carries
    // representative lng/lat points so PrintMode can frame-scope the legend.
    const printVisibleFeatures = useMemo(() => {
        if (!printOpen)
            return [];
        const cartToLngLat = (pos) => {
            if (!pos)
                return null;
            const c = Cesium.Cartographic.fromCartesian(pos);
            if (!c)
                return null;
            return { lng: Cesium.Math.toDegrees(c.longitude), lat: Cesium.Math.toDegrees(c.latitude) };
        };
        const compact = (pts) => pts.filter((p) => !!p);
        // Geometry for unsaved items lives on the live tool objects (Cartesian3),
        // keyed by id+type.
        const unsavedPositions = (id, type) => {
            var _a, _b, _c, _d;
            switch (type) {
                case 'placemark':
                    return compact([cartToLngLat((_a = placemarksTool.placemarks.get(id)) === null || _a === void 0 ? void 0 : _a.position)]);
                case 'path':
                    return compact((((_b = pathsTool.paths.get(id)) === null || _b === void 0 ? void 0 : _b.points) || []).map(p => cartToLngLat(p.position)));
                case 'polygon':
                    return compact((((_c = polygonTool.polygons.get(id)) === null || _c === void 0 ? void 0 : _c.vertices) || []).map(v => cartToLngLat(v.position)));
                case 'measurement': {
                    const line = measureTool.measurements.get(id);
                    if (line)
                        return compact([cartToLngLat(line.startPoint), cartToLngLat(line.endPoint)]);
                    const circle = measureTool.circles.get(id);
                    if (circle)
                        return compact([cartToLngLat(circle.center)]);
                    return [];
                }
                case 'contour_highlight':
                    return (((_d = contourHighlight.highlightedContours.get(id)) === null || _d === void 0 ? void 0 : _d.coordinates) || []).map(c => ({ lng: c.lng, lat: c.lat }));
                default:
                    return [];
            }
        };
        const savedItems = userFolders.folders.flatMap(f => f.artifacts.map(a => ({
            id: a.id,
            name: a.name,
            color: a.color,
            type: a.artifactType,
            icon: a.icon,
            ...parseSavedFeatureStyle(a.artifactType, a.geometryJson),
            positions: parseSavedFeaturePositions(a.artifactType, a.geometryJson),
        })));
        const unsavedItems = unsavedFacade.unsavedItems.map(it => {
            var _a;
            const poly = it.type === 'polygon' ? polygonTool.polygons.get(it.id) : undefined;
            return {
                ...it,
                icon: it.type === 'placemark' ? (_a = placemarksTool.placemarks.get(it.id)) === null || _a === void 0 ? void 0 : _a.icon : undefined,
                outlineColor: poly === null || poly === void 0 ? void 0 : poly.outlineColor,
                fillOpacity: poly === null || poly === void 0 ? void 0 : poly.fillOpacity,
                positions: unsavedPositions(it.id, it.type),
            };
        });
        return collectDrawnFeatures({ items: savedItems, isVisible: (id) => { var _a; return ((_a = userFolders.derivedVisibility.get(id)) === null || _a === void 0 ? void 0 : _a.visible) === true; } }, {
            items: unsavedItems,
            isVisible: (id, type) => unsavedFacade.handleIsUnsavedVisible(id, type),
        });
    }, [
        printOpen,
        userFolders.folders,
        userFolders.derivedVisibility,
        unsavedFacade.unsavedItems,
        unsavedFacade.handleIsUnsavedVisible,
        placemarksTool.placemarks,
        pathsTool.paths,
        polygonTool.polygons,
        measureTool.measurements,
        measureTool.circles,
        contourHighlight.highlightedContours,
    ]);
    useEffect(() => {
        const editing = serverArtifactEdit.editingState;
        if (!editing || !editing.unsavedItemId)
            return;
        // Sketch isn't tracked in the unsaved-items facade (it owns its own canvas
        // + Save changes / Cancel), so this "edited item vanished" guard doesn't
        // apply — without this it would cancel the sketch edit immediately.
        if (editing.artifactType === 'sketch')
            return;
        const stillUnsaved = unsavedFacade.unsavedItems.some(item => item.id === editing.unsavedItemId);
        if (!stillUnsaved) {
            serverArtifactEdit.cancelEdit();
        }
    }, [unsavedFacade.unsavedItems, serverArtifactEdit.editingState, serverArtifactEdit]);
    // Mirror of the cancelEdit effect for view-mode: when the locally-loaded
    // saved-path item disappears (user closed the dialog → handleDeleteUnsavedItem),
    // tear down view state and restore the server-rendered entity.
    useEffect(() => {
        const viewing = serverArtifactEdit.viewingState;
        if (!viewing || !viewing.unsavedItemId)
            return;
        const stillUnsaved = unsavedFacade.unsavedItems.some(item => item.id === viewing.unsavedItemId);
        if (!stillUnsaved) {
            serverArtifactEdit.cancelView();
        }
    }, [unsavedFacade.unsavedItems, serverArtifactEdit.viewingState, serverArtifactEdit]);
    // Mutually exclusive artifact selections: when the user clicks one artifact's
    // entity on the map, dismiss any other dialogs that were open. Empty-terrain
    // clicks don't dismiss — they fall through to the active tool's normal handler
    // (extending a path, etc.). This way the user can switch between artifacts
    // without their drawing context disappearing on a stray map click.
    const dismissOtherSelectionsExcept = useCallback((except) => {
        var _a;
        // If a saved-path/polygon view was active and we're switching to something
        // else, clean up the locally-loaded item (deletion triggers cancelView via
        // the existing effect).
        const vs = serverArtifactEdit.viewingState;
        if ((vs === null || vs === void 0 ? void 0 : vs.unsavedItemId) && (vs.artifactType === 'path' || vs.artifactType === 'polygon') && except !== vs.artifactType) {
            unsavedFacade.handleDeleteUnsavedItem(vs.unsavedItemId, vs.artifactType);
        }
        if (except !== 'path')
            pathsTool.dismissSelection();
        if (except !== 'polygon')
            polygonTool.dismissSelection();
        if (except !== 'placemark')
            placemarksTool.dismissSelection();
        if (except !== 'measurement') {
            measureTool.dismissSelection();
            (_a = measureTool.dismissCircleSelection) === null || _a === void 0 ? void 0 : _a.call(measureTool);
        }
        if (except !== 'label')
            labelsTool.dismissSelection();
        if (except !== 'serverArtifact')
            serverArtifactRenderer.dismissSelection();
    }, [serverArtifactEdit.viewingState, pathsTool, polygonTool, placemarksTool, measureTool, labelsTool, serverArtifactRenderer, unsavedFacade]);
    useEffect(() => {
        if (pathsTool.selectedPath)
            dismissOtherSelectionsExcept('path');
    }, [(_2 = pathsTool.selectedPath) === null || _2 === void 0 ? void 0 : _2.pathId]);
    useEffect(() => {
        if (polygonTool.selectedPolygon)
            dismissOtherSelectionsExcept('polygon');
    }, [(_3 = polygonTool.selectedPolygon) === null || _3 === void 0 ? void 0 : _3.polygonId]);
    useEffect(() => {
        if (placemarksTool.selectedPlacemark)
            dismissOtherSelectionsExcept('placemark');
    }, [(_4 = placemarksTool.selectedPlacemark) === null || _4 === void 0 ? void 0 : _4.placemarkId]);
    useEffect(() => {
        var _a;
        if (measureTool.selectedMeasurement) {
            dismissOtherSelectionsExcept('measurement');
            (_a = measureTool.dismissCircleSelection) === null || _a === void 0 ? void 0 : _a.call(measureTool);
        }
    }, [(_5 = measureTool.selectedMeasurement) === null || _5 === void 0 ? void 0 : _5.measurementId]);
    useEffect(() => {
        if (measureTool.selectedCircle) {
            dismissOtherSelectionsExcept('measurement');
            measureTool.dismissSelection();
        }
    }, [(_6 = measureTool.selectedCircle) === null || _6 === void 0 ? void 0 : _6.measurementId]);
    useEffect(() => {
        if (labelsTool.selectedLabel)
            dismissOtherSelectionsExcept('label');
    }, [(_7 = labelsTool.selectedLabel) === null || _7 === void 0 ? void 0 : _7.labelId]);
    useEffect(() => {
        if (serverArtifactRenderer.selectedArtifact)
            dismissOtherSelectionsExcept('serverArtifact');
    }, [(_9 = (_8 = serverArtifactRenderer.selectedArtifact) === null || _8 === void 0 ? void 0 : _8.artifact) === null || _9 === void 0 ? void 0 : _9.id]);
    const editingPath = useMemo(() => {
        const e = serverArtifactEdit.editingState;
        return (e === null || e === void 0 ? void 0 : e.artifactType) === 'path' && e.unsavedItemId ? pathsTool.paths.get(e.unsavedItemId) : undefined;
    }, [serverArtifactEdit.editingState, pathsTool.paths]);
    const editingPolygon = useMemo(() => {
        const e = serverArtifactEdit.editingState;
        return (e === null || e === void 0 ? void 0 : e.artifactType) === 'polygon' && e.unsavedItemId ? polygonTool.polygons.get(e.unsavedItemId) : undefined;
    }, [serverArtifactEdit.editingState, polygonTool.polygons]);
    const editingItemName = (_10 = editingPath === null || editingPath === void 0 ? void 0 : editingPath.name) !== null && _10 !== void 0 ? _10 : editingPolygon === null || editingPolygon === void 0 ? void 0 : editingPolygon.name;
    const visibleUnsavedItems = useMemo(() => {
        var _a, _b;
        // Hide the transient copy a saved artifact is loaded into while it's being
        // VIEWED or EDITED — it's not a real unsaved drawing, and showing it in the
        // "Unsaved drawings" list makes a saved item look unsaved (confusing).
        const editId = (_a = serverArtifactEdit.editingState) === null || _a === void 0 ? void 0 : _a.unsavedItemId;
        const viewId = (_b = serverArtifactEdit.viewingState) === null || _b === void 0 ? void 0 : _b.unsavedItemId;
        // A saved sketch loaded for editing occupies the same canvas the unsaved
        // sketch row reads, and it carries its own edit id, so drop the row while
        // that edit is live: saving from here would fork a second artifact.
        const items = isEditingSketch
            ? unsavedFacade.unsavedItems.filter(i => i.type !== 'sketch')
            : unsavedFacade.unsavedItems;
        if (!editId && !viewId)
            return items;
        return items.filter(i => i.id !== editId && i.id !== viewId);
    }, [unsavedFacade.unsavedItems, serverArtifactEdit.editingState, serverArtifactEdit.viewingState, isEditingSketch]);
    // Artifact promotion — save-to-folder, placemark from coordinates, path file import
    const promotion = useArtifactPromotion({
        tools: { placemarksTool, measureTool, pathsTool, polygonTool, contourHighlight, streamHighlight, labelsTool },
        userFolders,
        projectId,
        addStatus: grpcService.addStatus,
        onDeactivateTool: () => markupMode.setTool(null),
        editingState: serverArtifactEdit.editingState,
        onEditComplete: () => serverArtifactEdit.cancelEdit(),
    });
    // Ref to the latest handleAddItemToFolder so deferred saves (commitFirst's
    // setTimeout in the label popout) call the version that closes over the
    // post-commit labels Map — not the stale one captured before commit ran.
    const handleAddItemToFolderRef = useRef(promotion.handleAddItemToFolder);
    useEffect(() => { handleAddItemToFolderRef.current = promotion.handleAddItemToFolder; });
    // Saving changes to an already-saved artifact commits from a dialog that
    // closes on the click, so there is no form left to hold a message. Those
    // rejections land here and render above the toolbar until the user dismisses
    // them or tries again; the edited copy stays on the map for the retry.
    const [artifactEditSaveError, setArtifactEditSaveError] = useState(null);
    const runServerArtifactSave = useCallback((run) => {
        setArtifactEditSaveError(null);
        run().catch((err) => setArtifactEditSaveError(messageFromSaveError(err)));
    }, []);
    // Save from an Unsaved Drawings row. The sketch canvas persists as one
    // artifact through handleSaveSketch (the same path the Finish modal uses);
    // every other type goes through artifact promotion.
    const handleSaveUnsavedItem = useCallback((itemId, itemType, folderId) => {
        if (itemType === 'sketch') {
            return handleSaveSketch(SKETCH_ARTIFACT_NAME, folderId);
        }
        return promotion.handleAddItemToFolder(itemId, itemType, folderId);
    }, [handleSaveSketch, promotion]);
    // Saving (or cancelling an edit of) a path removes its local copy, which
    // strips the path's popped-out profiles — but the profile should survive
    // in place, now backed by the saved artifact. Capture the popouts (with
    // their latest layouts) into a pending handover; the effect below re-opens
    // them on the server-artifact rail once the artifact is in the folder tree.
    const [pendingPathPopoutHandover, setPendingPathPopoutHandover] = useState(null);
    const capturePathPopoutsForHandover = useCallback((pathId, savedArtifactId) => {
        const entries = pathsTool.poppedOutProfiles
            .filter(p => p.pathId === pathId)
            .map(p => {
            var _a, _b, _c;
            const layoutKey = `${p.pathId}:${(_a = p.segmentIndex) !== null && _a !== void 0 ? _a : 'total'}`;
            const layout = pathPopoutLayoutsRef.current.get(layoutKey);
            pathPopoutLayoutsRef.current.delete(layoutKey);
            return {
                position: (_b = layout === null || layout === void 0 ? void 0 : layout.position) !== null && _b !== void 0 ? _b : p.position,
                segmentIndex: layout ? (layout.view === 'total' ? null : layout.view) : ((_c = p.segmentIndex) !== null && _c !== void 0 ? _c : null),
                width: layout === null || layout === void 0 ? void 0 : layout.width,
            };
        });
        if (entries.length > 0) {
            setPendingPathPopoutHandover({ savedArtifactId, popouts: entries });
        }
    }, [pathsTool.poppedOutProfiles]);
    // Fresh unsaved paths: the promotion event fires with the new artifact id
    // just before the local path (and its popouts) are removed. Covers saves
    // from the popout's embedded save section and from the dialog alike.
    useEventSubscribe('userArtifact:promoted', (payload) => {
        if (payload.savedArtifactType !== 'path')
            return;
        capturePathPopoutsForHandover(payload.unsavedItemId, payload.savedArtifactId);
    }, [capturePathPopoutsForHandover]);
    useEffect(() => {
        if (!pendingPathPopoutHandover)
            return;
        const artifact = userFolders.folders
            .flatMap(f => f.artifacts)
            .find(a => a.id === pendingPathPopoutHandover.savedArtifactId);
        if (!artifact)
            return;
        pendingPathPopoutHandover.popouts.forEach(p => {
            serverArtifactRenderer.popOutArtifact(artifact, p.position, p.segmentIndex, p.width);
        });
        setPendingPathPopoutHandover(null);
    }, [pendingPathPopoutHandover, userFolders.folders, serverArtifactRenderer]);
    // Saved-path edit Save/Cancel, shared by the edit dialog and the floating
    // edit pill. Closing the dialog mid-edit keeps the session alive (nodes
    // stay draggable, popped-out profiles keep updating), so these must be
    // reachable without the dialog. Both hand the popouts over to the saved
    // artifact (the update flow doesn't emit the promotion event).
    const handleSavePathServerEdit = useCallback(() => {
        const s = serverArtifactEdit.editingState;
        if ((s === null || s === void 0 ? void 0 : s.artifactType) !== 'path' || !s.unsavedItemId)
            return;
        capturePathPopoutsForHandover(s.unsavedItemId, s.serverArtifactId);
        markupMode.setTool(null);
        runServerArtifactSave(() => promotion.handleAddItemToFolder(s.unsavedItemId, 'path', s.folderId));
    }, [serverArtifactEdit.editingState, markupMode, promotion, capturePathPopoutsForHandover, runServerArtifactSave]);
    const handleCancelPathServerEdit = useCallback(() => {
        const s = serverArtifactEdit.editingState;
        if ((s === null || s === void 0 ? void 0 : s.artifactType) !== 'path' || !s.unsavedItemId)
            return;
        capturePathPopoutsForHandover(s.unsavedItemId, s.serverArtifactId);
        markupMode.setTool(null);
        unsavedFacade.handleDeleteUnsavedItem(s.unsavedItemId, 'path');
    }, [serverArtifactEdit.editingState, markupMode, unsavedFacade, capturePathPopoutsForHandover]);
    // Reverse popout handover: when a saved path is loaded locally (view or
    // edit), migrate its self-sufficient server-rail popouts onto the live
    // local path so they follow node edits instead of freezing on the saved
    // geometry. (The view-close handover in pathServerView.onClose does the
    // opposite direction.) Idempotent: popOutProfile dedupes and the server
    // popout is removed on migration, so re-runs find nothing to do.
    useEffect(() => {
        var _a, _b;
        const s = ((_a = serverArtifactEdit.editingState) === null || _a === void 0 ? void 0 : _a.artifactType) === 'path'
            ? serverArtifactEdit.editingState
            : ((_b = serverArtifactEdit.viewingState) === null || _b === void 0 ? void 0 : _b.artifactType) === 'path'
                ? serverArtifactEdit.viewingState
                : null;
        if (!(s === null || s === void 0 ? void 0 : s.unsavedItemId))
            return;
        const migrating = serverArtifactRenderer.poppedOutArtifacts
            .filter(p => p.artifact.id === s.serverArtifactId);
        migrating.forEach(p => {
            var _a, _b;
            serverArtifactRenderer.closePopOutArtifact(p.artifact.id, (_a = p.segmentIndex) !== null && _a !== void 0 ? _a : null);
            pathsTool.popOutProfile(s.unsavedItemId, (_b = p.segmentIndex) !== null && _b !== void 0 ? _b : null, p.position);
        });
    }, [serverArtifactEdit.editingState, serverArtifactEdit.viewingState, serverArtifactRenderer.poppedOutArtifacts, serverArtifactRenderer, pathsTool]);
    // Keyboard shortcuts for all tool interactions
    useToolKeyboardShortcuts({
        pathExtending: pathsTool.extendingPath,
        pathStopExtending: pathsTool.stopExtending,
        polygonDeleteVertexPrompt: polygonTool.deleteVertexPrompt,
        polygonConfirmDeleteVertex: polygonTool.confirmDeleteVertex,
        polygonCancelDeleteVertex: polygonTool.cancelDeleteVertex,
        activeSpider: placemarkClustering.activeSpider,
        dismissSpider: placemarkClustering.dismissSpider,
        overlappingPolygons: polygonTool.overlappingPolygons,
        dismissOverlapPicker: polygonTool.dismissOverlapPicker,
    });
    // Delete-key shortcut: remove the selected label when no input field
    // has focus. We deliberately use a ref so the listener doesn't have to
    // re-subscribe whenever labelsTool's identity changes each render.
    const labelsToolRef = useRef(labelsTool);
    useEffect(() => { labelsToolRef.current = labelsTool; });
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key !== 'Delete')
                return;
            const target = e.target;
            if (target) {
                const tag = target.tagName;
                if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable)
                    return;
            }
            const tool = labelsToolRef.current;
            const sel = tool.selectedLabel;
            if (!sel)
                return;
            // Don't fire while the user is typing — the inline editor's blur
            // handler may have just stolen focus from the textarea on a click
            // outside but the editing state can still be live.
            if (tool.editingLabel)
                return;
            e.preventDefault();
            tool.removeLabel(sel.labelId);
            tool.dismissSelection();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);
    // Project polygon drawing (project creation boundary drawing). Declared
    // BEFORE useGlobalKeyboardShortcuts so `isPolygonDrawing` is initialised
    // by the time the shortcut hook reads it for `isDrawingActive`. Prior
    // ordering hit the TDZ at runtime.
    const polygonDrawing = useProjectPolygonDrawing(viewer, modalActions, {
        clearAllArtifacts: grpcService.clearAllArtifacts,
        clearAllMeasurements: measureTool.clearAllMeasurements,
        unhighlightAllContours: contourHighlight.unhighlightAll,
    });
    const { isPolygonDrawing, polygonProjectName, polygonProjectDescription, handleStartPolygonDrawing, handlePolygonDrawingFinish, handlePolygonDrawingCancel, handleEmailVerificationSet } = polygonDrawing;
    // A boundary-upload (KML/GeoJSON/shapefile) create pre-populates the drawing
    // session with the uploaded vertices, and that flow can finish without ever
    // exiting drawing mode — leaving those red boundary nodes stranded in the
    // 'polygon-drawing' Cesium data source until a refresh tears it down. Force
    // drawing mode closed on any successful create so the orchestrator unmounts
    // and the hook's cleanup removes that data source immediately. No-op for the
    // hand-draw flow, which already exits drawing mode on finish.
    useEventSubscribe('project:created', () => {
        if (isPolygonDrawing)
            handlePolygonDrawingFinish('', '', []);
    }, [isPolygonDrawing, handlePolygonDrawingFinish]);
    // Global navigation + tool-switching shortcuts (Tier 1, Google-Earth-Pro parity)
    useGlobalKeyboardShortcuts({
        viewer,
        activeTool: markupMode.activeTool,
        setTool: markupMode.setTool,
        selectedProject: grpcService.selectedProject,
        modalOpen: modalState.activeModal !== null,
        isDrawingActive: !!pathsTool.pendingPath ||
            polygonTool.isDrawing ||
            isPolygonDrawing,
    });
    // Auth navigation (fly-to on login, reset on logout, login modal)
    useAuthNavigation(viewer, isAuthenticated, isLoading, modalActions);
    // Apply opacity and weight changes to Cesium artifacts
    useEffect(() => {
        if (!viewer)
            return;
        grpcService.artifactControls.forEach((controls, artifactId) => {
            var _a, _b;
            const artifact = grpcService.loadedArtifacts.get(artifactId);
            if (artifact) {
                if (artifact.type === 'raster') {
                    // For imagery layers, update the alpha property directly
                    if (artifact.cesiumObject && artifact.cesiumObject.alpha !== undefined) {
                        artifact.cesiumObject.alpha = controls.opacity;
                    }
                }
                else if (artifact.type === 'vector') {
                    // Monochrome contour mode owns this layer's polyline material + width
                    // (solid index / dashed intermediate). Route through the transform and
                    // skip the generic recolour below, which would flatten the hierarchy
                    // and turn dashes solid.
                    if ((_a = controls.monochrome) === null || _a === void 0 ? void 0 : _a.enabled) {
                        const layerName = typeof ((_b = artifact.metadata) === null || _b === void 0 ? void 0 : _b.getName) === 'function'
                            ? artifact.metadata.getName()
                            : '';
                        applyMonochromeContours(artifact.cesiumObject, layerName, controls.monochrome.color, controls.opacity, controls.weight);
                    }
                    else {
                        // If this layer was previously in monochrome mode, restore the baked
                        // elevation-ramp appearance before applying generic opacity/weight.
                        restoreBakedContours(artifact.cesiumObject);
                        // For vector data sources, update material alpha and weight
                        const entities = artifact.cesiumObject.entities.values;
                        entities.forEach((entity) => {
                            var _a, _b;
                            // A contour highlight owns this entity's appearance — don't clobber it.
                            if (entity[HIGHLIGHTED_CONTOUR_FLAG])
                                return;
                            if (entity.polygon && entity.polygon.material) {
                                const currentColor = ((_a = entity.polygon.material.color) === null || _a === void 0 ? void 0 : _a.getValue()) || entity.polygon.material;
                                if (currentColor && currentColor.withAlpha) {
                                    entity.polygon.material = currentColor.withAlpha(controls.opacity);
                                }
                            }
                            if (entity.polyline && entity.polyline.material) {
                                const currentColor = ((_b = entity.polyline.material.color) === null || _b === void 0 ? void 0 : _b.getValue()) || entity.polyline.material;
                                if (currentColor && currentColor.withAlpha) {
                                    entity.polyline.material = currentColor.withAlpha(controls.opacity);
                                }
                                if (controls.weight) {
                                    entity.polyline.width = controls.weight;
                                }
                            }
                            if (entity.polygon && entity.polygon.outline && controls.weight) {
                                entity.polygon.outlineWidth = controls.weight;
                            }
                        });
                    }
                }
            }
        });
    }, [viewer, grpcService.artifactControls, grpcService.loadedArtifacts]);
    // Memoized initialization function to prevent recreation
    const initializeViewer = useMemo(() => {
        return async (isCancelled) => {
            try {
                const newViewer = new Cesium.Viewer('cesiumContainer', {
                    terrainProvider: new Cesium.EllipsoidTerrainProvider(),
                    homeButton: false,
                    sceneModePicker: false,
                    baseLayerPicker: false,
                    navigationHelpButton: false,
                    animation: false,
                    timeline: false,
                    fullscreenButton: false,
                    vrButton: false,
                    // OIT requires float-buffer/MRT support that some GPUs botch (translucent
                    // contour lines render invisible). Must be set at construction time —
                    // scene.orderIndependentTranslucency is read-only after creation.
                    orderIndependentTranslucency: false
                });
                // Publish the handle before the first await. The effect's cleanup
                // destroys whatever this ref holds; unmounting while the imagery
                // provider is still loading would otherwise strand a live viewer
                // (WebGL context + render loop) for the rest of the page's life.
                viewerRef.current = newViewer;
                newViewer.resolutionScale = window.devicePixelRatio;
                // Remove the default imagery layer
                newViewer.imageryLayers.removeAll();
                // Configure Cesium Ion access token - clear any existing token first
                Cesium.Ion.defaultAccessToken = '';
                Cesium.Ion.defaultAccessToken = REACT_APP_CESIUM_ION_TOKEN;
                // Add Bing aerial basemap via Cesium Ion - explicitly pass the access token
                const imageryProvider = await Cesium.IonImageryProvider.fromAssetId(BING_ARIAL_ASSET_ID, {
                    accessToken: REACT_APP_CESIUM_ION_TOKEN
                });
                if (isCancelled() || newViewer.isDestroyed())
                    return;
                newViewer.imageryLayers.addImageryProvider(imageryProvider);
                // Hide credits
                newViewer.cesiumWidget.creditContainer.style.display = "none";
                // Set initial neutral global view for waitlist/onboarding experience
                newViewer.camera.setView({
                    destination: Cesium.Cartesian3.fromDegrees(0.0, 0.0, 10500000), // Zoomed in global view (30% closer)
                    orientation: {
                        heading: Cesium.Math.toRadians(0.0),
                        pitch: Cesium.Math.toRadians(-90.0),
                        roll: 0.0
                    }
                });
                // Enable lighting for visual appeal
                newViewer.scene.globe.enableLighting = true;
                // Configure memory-efficient tile caching
                newViewer.scene.globe.tileCacheSize = 200; // Increase from default 100 for better caching
                // Configure Level of Detail for better performance/memory balance
                newViewer.scene.screenSpaceCameraController.maximumZoomDistance = 40075000; // Earth circumference
                newViewer.scene.screenSpaceCameraController.minimumZoomDistance = 1.0;
                // Mouse move handling is set up in a useEffect to use hooks properly
                setViewer(newViewer);
                setIsInitialized(true);
            }
            catch (error) {
                console.error('Cesium error:', error);
            }
        };
    }, []);
    useEffect(() => {
        let cancelled = false;
        // Wait for DOM to be ready
        const timeout = setTimeout(() => { void initializeViewer(() => cancelled); }, 200);
        return () => {
            cancelled = true;
            clearTimeout(timeout);
            if (viewerRef.current && !viewerRef.current.isDestroyed()) {
                viewerRef.current.destroy();
            }
            viewerRef.current = null;
        };
    }, [initializeViewer]);
    // Central event router — routes all Cesium map interactions to tool hooks
    useCesiumEventRouter({
        viewer,
        pathsTool,
        placemarksTool,
        labelsTool,
        measureTool,
        polygonTool,
        contourHighlight,
        streamHighlight,
        placemarkClustering,
        serverArtifactRenderer,
        commentPins,
        // Print mode is a final draft — lock out all map interaction (clicks,
        // selection, markup). Pan/zoom (Cesium camera controller) is unaffected, so
        // the user can still compose. See PRINT_VIEW_PLAN.md.
        interactionEnabled: inProject && !printOpen,
        markupIsActive: markupMode.isActive,
        isPathsToolActive,
        isPlacemarkToolActive,
        isMeasureToolActive,
        isPolygonToolActive,
        isLabelToolActive,
        isCommentToolActive,
        bufferEnabled,
        bufferDistance,
        bufferUnit,
        bufferError,
        onSetHoveredDraggable: setHoveredDraggable,
        onSetHoveredPolygonBody: setHoveredPolygonBody,
        onSetHoveredPlacemark: setHoveredPlacemark,
        hoveredPlacemarkEntityRef,
    });
    // Cursor management — 7-level priority chain for all tool states
    useCursorManager(viewer, {
        markupIsActive: markupMode.isActive,
        hoveredDraggable,
        hoveredPolygonBody,
        pathsDragging: pathsTool.dragging,
        pathsDraggingPending: pathsTool.draggingPending,
        pathsExtending: !!pathsTool.extendingPath,
        measureDragging: measureTool.dragging,
        placemarksDragging: placemarksTool.dragging,
        labelsDragging: labelsTool.dragging,
        polygonDragging: polygonTool.dragging,
        polygonDrawingDragging: polygonTool.drawingIsDragging,
        isPlacemarkToolActive,
        isMeasureToolActive,
        isPathsToolActive,
        isPolygonToolActive,
        isLabelToolActive,
        isCommentToolActive,
        contourHighlightModeEnabled,
        streamHighlightModeEnabled,
        hoveredContour: contourHighlight.hoveredContour,
        hoveredStream: streamHighlight.hoveredStream,
    });
    return (_jsxs("div", { className: "relative w-full h-full", children: [_jsx("div", { id: "cesiumContainer", className: "w-full h-full" }), _jsx(DemoRestrictionModal, { isOpen: printRestricted, onClose: () => setPrintRestricted(false), action: "print" }), viewer && (_jsx(PrintMode, { viewer: viewer, isOpen: printOpen, onClose: () => setPrintOpen(false), projectName: (_12 = (_11 = grpcService.selectedProject) === null || _11 === void 0 ? void 0 : _11.getName) === null || _12 === void 0 ? void 0 : _12.call(_11), loadedArtifacts: grpcService.loadedArtifacts, isArtifactVisible: grpcService.isArtifactVisible, selectedProject: grpcService.selectedProject, artifactControls: grpcService.artifactControls, visibleFeatures: printVisibleFeatures })), viewer && (_jsx(MapAttribution, { currentBasemap: grpcService.currentBasemap })), viewer && (_jsx(LegendOverlay, { viewer: viewer, loadedArtifacts: grpcService.loadedArtifacts, isArtifactVisible: grpcService.isArtifactVisible, selectedProject: grpcService.selectedProject, isPolygonDrawing: isPolygonDrawing, artifactControls: grpcService.artifactControls })), _jsx(HighlightModeProvider, { value: {
                    contourEnabled: contourHighlightModeEnabled,
                    streamEnabled: streamHighlightModeEnabled,
                    setContourEnabled: setContourHighlightModeEnabled,
                    setStreamEnabled: setStreamHighlightModeEnabled,
                }, children: _jsx(MapViewerWithContext, { viewer: viewer, grpcService: grpcService, isPolygonDrawing: isPolygonDrawing, 
                    // Consolidated modal state/actions
                    modalState: modalState, modalActions: modalActions, 
                    // Project refresh function
                    refetchUserProjects: refetchUserProjects, 
                    // DEM upload state
                    isDemUploading: isDemUploading, 
                    // Markup mode
                    activeTool: markupMode.activeTool, onSetTool: markupMode.setTool, 
                    // Unsaved items (filtered to exclude any item currently being edited)
                    unsavedItems: visibleUnsavedItems, onSaveItem: handleSaveUnsavedItem, onDeleteItem: unsavedFacade.handleDeleteUnsavedItem, onToggleUnsavedVisibility: unsavedFacade.handleToggleUnsavedVisibility, isUnsavedItemVisible: unsavedFacade.handleIsUnsavedVisible, 
                    // Scopes
                    scopes: scopesHook.scopes, scopesLoading: scopesHook.loading, activeScopeId: scopesHook.activeScopeId, onCaptureScope: scopesHook.captureScope, onApplyScope: scopesHook.applyScope, onClearScope: scopesHook.clearScope, onDeleteScope: scopesHook.deleteScope, onOverrideScope: scopesHook.overrideScope, onRenameScope: scopesHook.renameScope, onDuplicateScope: scopesHook.duplicateScope, onReorderScopes: scopesHook.reorderScopes, 
                    // User folders
                    userFolders: userFolders, onShowAccountModal: (data) => { setAccountInitialData(data !== null && data !== void 0 ? data : null); modalActions.openAccount(); }, 
                    // Project bounds toggle
                    boundsVisible: boundsVisible, onToggleBoundsVisible: () => setBoundsVisible(v => !v), onShowInfo: (artifact, position) => serverArtifactRenderer.selectArtifact(artifact, position), onFlyToArtifact: (artifactId) => serverArtifactRenderer.flyToArtifact(artifactId), onEditArtifact: (artifact) => serverArtifactEdit.startEdit(artifact), onDuplicateArtifact: (artifact) => userFolders.duplicateArtifact(artifact), onMoveArtifact: (artifactId, newFolderId) => userFolders.moveArtifact(artifactId, newFolderId) }) }), !isPolygonDrawing && !isDemUploading && (_jsx(MapControlPanel, { viewer: viewer, currentBasemap: grpcService.currentBasemap, onChangeBasemap: grpcService.changeBasemap, selectedProject: grpcService.selectedProject })), !isPolygonDrawing && !isDemUploading && _jsx(DirectionsPanel, {}), _jsx(OnboardingTour, {}), _jsx(InfoPanel, { panels: grpcService.activeInfoPanels, onClose: grpcService.closeInfoPanel, projectId: projectId, isDemoProject: !!projectId && !userProjects.some(up => up.id === projectId) }), _jsx(ScreenOverlay, { overlays: grpcService.activeScreenOverlays }), _jsx(MapOverlays, { contourHighlight: contourHighlight, streamHighlight: streamHighlight, userFolders: userFolders.folders, onCreateFolder: userFolders.createFolder, onSaveItem: promotion.handleAddItemToFolder, serverEditingState: serverArtifactEdit.editingState, onStreamPromoteViewToEdit: serverArtifactEdit.promoteViewToEdit, onDeactivateTool: () => {
                    markupMode.setTool(null);
                    // If the dialog opened over a server-saved artifact (via
                    // right-click → Edit or click → View), bring the saved
                    // rendering back. Safe to call when not in edit/view mode —
                    // cancelEdit / cancelView are no-ops there.
                    if (serverArtifactEdit.editingState) {
                        serverArtifactEdit.cancelEdit();
                    }
                    if (serverArtifactEdit.viewingState) {
                        serverArtifactEdit.cancelView();
                    }
                } }), _jsx(ToolDialogs, { measureTool: measureTool, pathsTool: pathsTool, placemarksTool: placemarksTool, polygonTool: polygonTool, markupIsActive: markupMode.isActive, markupSetTool: markupMode.setTool, polygonStats: polygonStats, handleProfileHoverPosition: handleProfileHoverPosition, measurementCrosshairFraction: measurementCrosshairFraction, pathCrosshairFraction: pathCrosshairFraction, unitPreferences: unitPreferences, pathServerEdit: {
                    isEditing: ((_13 = serverArtifactEdit.editingState) === null || _13 === void 0 ? void 0 : _13.artifactType) === 'path'
                        && ((_14 = pathsTool.selectedPath) === null || _14 === void 0 ? void 0 : _14.pathId) === editingPathId,
                    serverArtifactId: (_15 = serverArtifactEdit.editingState) === null || _15 === void 0 ? void 0 : _15.serverArtifactId,
                    onSave: handleSavePathServerEdit,
                    onCancel: handleCancelPathServerEdit,
                    onDelete: () => {
                        // Hard-delete the saved path: discard the editable copy, remove the
                        // server artifact, refresh the folder tree.
                        const s = serverArtifactEdit.editingState;
                        if (!(s === null || s === void 0 ? void 0 : s.serverArtifactId))
                            return;
                        markupMode.setTool(null);
                        if (s.unsavedItemId)
                            unsavedFacade.handleDeleteUnsavedItem(s.unsavedItemId, 'path');
                        userFolders.deleteArtifact(s.serverArtifactId)
                            .then(() => userFolders.fetchFolders())
                            .catch((err) => { console.warn('[MapViewer] delete saved path failed:', err); });
                    },
                }, polygonServerEdit: {
                    isEditing: ((_16 = serverArtifactEdit.editingState) === null || _16 === void 0 ? void 0 : _16.artifactType) === 'polygon'
                        && ((_17 = polygonTool.selectedPolygon) === null || _17 === void 0 ? void 0 : _17.polygonId) === editingPolygonId,
                    serverArtifactId: (_18 = serverArtifactEdit.editingState) === null || _18 === void 0 ? void 0 : _18.serverArtifactId,
                    onSave: () => {
                        const s = serverArtifactEdit.editingState;
                        if (!(s === null || s === void 0 ? void 0 : s.unsavedItemId))
                            return;
                        markupMode.setTool(null);
                        runServerArtifactSave(() => promotion.handleAddItemToFolder(s.unsavedItemId, 'polygon', s.folderId));
                    },
                    onCancel: () => {
                        const s = serverArtifactEdit.editingState;
                        if (!(s === null || s === void 0 ? void 0 : s.unsavedItemId))
                            return;
                        markupMode.setTool(null);
                        unsavedFacade.handleDeleteUnsavedItem(s.unsavedItemId, 'polygon');
                    },
                    onDelete: () => {
                        // Hard-delete the saved polygon: discard the in-progress editable
                        // copy, remove the server artifact (DELETE FROM user_artifacts), and
                        // refresh the folder tree so it disappears everywhere.
                        const s = serverArtifactEdit.editingState;
                        if (!(s === null || s === void 0 ? void 0 : s.serverArtifactId))
                            return;
                        markupMode.setTool(null);
                        if (s.unsavedItemId)
                            unsavedFacade.handleDeleteUnsavedItem(s.unsavedItemId, 'polygon');
                        userFolders.deleteArtifact(s.serverArtifactId)
                            .then(() => userFolders.fetchFolders())
                            .catch((err) => { console.warn('[MapViewer] delete saved polygon failed:', err); });
                    },
                }, placemarkServerEdit: {
                    isEditing: ((_19 = serverArtifactEdit.editingState) === null || _19 === void 0 ? void 0 : _19.artifactType) === 'placemark'
                        && ((_20 = placemarksTool.selectedPlacemark) === null || _20 === void 0 ? void 0 : _20.placemarkId) === ((_21 = serverArtifactEdit.editingState) === null || _21 === void 0 ? void 0 : _21.unsavedItemId),
                    serverArtifactId: (_22 = serverArtifactEdit.editingState) === null || _22 === void 0 ? void 0 : _22.serverArtifactId,
                    onSave: () => {
                        const s = serverArtifactEdit.editingState;
                        if (!(s === null || s === void 0 ? void 0 : s.unsavedItemId))
                            return;
                        markupMode.setTool(null);
                        runServerArtifactSave(() => promotion.handleAddItemToFolder(s.unsavedItemId, 'placemark', s.folderId));
                    },
                    onCancel: () => {
                        const s = serverArtifactEdit.editingState;
                        if (!(s === null || s === void 0 ? void 0 : s.unsavedItemId))
                            return;
                        markupMode.setTool(null);
                        unsavedFacade.handleDeleteUnsavedItem(s.unsavedItemId, 'placemark');
                    },
                }, measurementServerEdit: {
                    isEditing: ((_23 = serverArtifactEdit.editingState) === null || _23 === void 0 ? void 0 : _23.artifactType) === 'measurement'
                        && ((_24 = measureTool.selectedMeasurement) === null || _24 === void 0 ? void 0 : _24.measurementId) === ((_25 = serverArtifactEdit.editingState) === null || _25 === void 0 ? void 0 : _25.unsavedItemId),
                    serverArtifactId: (_26 = serverArtifactEdit.editingState) === null || _26 === void 0 ? void 0 : _26.serverArtifactId,
                    onSave: () => {
                        const s = serverArtifactEdit.editingState;
                        if (!(s === null || s === void 0 ? void 0 : s.unsavedItemId))
                            return;
                        markupMode.setTool(null);
                        runServerArtifactSave(() => promotion.handleAddItemToFolder(s.unsavedItemId, 'measurement', s.folderId));
                    },
                    onCancel: () => {
                        const s = serverArtifactEdit.editingState;
                        if (!(s === null || s === void 0 ? void 0 : s.unsavedItemId))
                            return;
                        markupMode.setTool(null);
                        unsavedFacade.handleDeleteUnsavedItem(s.unsavedItemId, 'measurement');
                    },
                }, pathServerView: {
                    isViewing: ((_27 = serverArtifactEdit.viewingState) === null || _27 === void 0 ? void 0 : _27.artifactType) === 'path'
                        && ((_28 = pathsTool.selectedPath) === null || _28 === void 0 ? void 0 : _28.pathId) === ((_29 = serverArtifactEdit.viewingState) === null || _29 === void 0 ? void 0 : _29.unsavedItemId),
                    serverArtifactId: (_30 = serverArtifactEdit.viewingState) === null || _30 === void 0 ? void 0 : _30.serverArtifactId,
                    onPromoteToEdit: () => serverArtifactEdit.promoteViewToEdit(),
                    onClose: () => {
                        const s = serverArtifactEdit.viewingState;
                        if (!(s === null || s === void 0 ? void 0 : s.unsavedItemId)) {
                            pathsTool.dismissSelection();
                            return;
                        }
                        // Popped-out profiles read from the locally-loaded path, which is
                        // about to be deleted. Hand them over to the self-sufficient
                        // server-artifact popout rail so they outlive the view dialog.
                        const savedArtifact = s.serverArtifactId
                            ? userFolders.folders.flatMap(f => f.artifacts).find(a => a.id === s.serverArtifactId)
                            : undefined;
                        if (savedArtifact) {
                            pathsTool.poppedOutProfiles
                                .filter(p => p.pathId === s.unsavedItemId)
                                .forEach(p => {
                                var _a, _b;
                                const layoutKey = `${p.pathId}:${(_a = p.segmentIndex) !== null && _a !== void 0 ? _a : 'total'}`;
                                const layout = pathPopoutLayoutsRef.current.get(layoutKey);
                                pathPopoutLayoutsRef.current.delete(layoutKey);
                                const segmentIndex = layout ? (layout.view === 'total' ? null : layout.view) : p.segmentIndex;
                                serverArtifactRenderer.popOutArtifact(savedArtifact, (_b = layout === null || layout === void 0 ? void 0 : layout.position) !== null && _b !== void 0 ? _b : p.position, segmentIndex, layout === null || layout === void 0 ? void 0 : layout.width);
                            });
                        }
                        // Removing the locally-loaded item triggers the cancel-view useEffect,
                        // which restores the server-rendered entity and clears state.
                        unsavedFacade.handleDeleteUnsavedItem(s.unsavedItemId, 'path');
                    },
                }, polygonServerView: {
                    isViewing: ((_31 = serverArtifactEdit.viewingState) === null || _31 === void 0 ? void 0 : _31.artifactType) === 'polygon'
                        && ((_32 = polygonTool.selectedPolygon) === null || _32 === void 0 ? void 0 : _32.polygonId) === ((_33 = serverArtifactEdit.viewingState) === null || _33 === void 0 ? void 0 : _33.unsavedItemId),
                    serverArtifactId: (_34 = serverArtifactEdit.viewingState) === null || _34 === void 0 ? void 0 : _34.serverArtifactId,
                    onPromoteToEdit: () => serverArtifactEdit.promoteViewToEdit(),
                    onClose: () => {
                        const s = serverArtifactEdit.viewingState;
                        if (!(s === null || s === void 0 ? void 0 : s.unsavedItemId)) {
                            polygonTool.dismissSelection();
                            return;
                        }
                        unsavedFacade.handleDeleteUnsavedItem(s.unsavedItemId, 'polygon');
                    },
                }, inlineSave: {
                    userFolders: userFolders.folders,
                    onCreateFolder: userFolders.createFolder,
                    onSaveItem: promotion.handleAddItemToFolder,
                } }), _jsx(ConfirmationPrompts, { hoveredPlacemark: hoveredPlacemark, placemarkClustering: placemarkClustering, placemarkSelectById: placemarksTool.selectPlacemarkById, polygonTool: polygonTool, pathsTool: pathsTool, markupActiveTool: markupMode.activeTool, markupInstruction: isSketchToolActive
                    ? `${sketchMode === 'eraser' ? 'Drag to erase' : 'Drag to draw'}. Arrow keys or two fingers move the map; scroll or pinch to zoom.`
                    : markupMode.instruction }), _jsx(LabelMapOverlay, { viewer: viewer, labels: [
                    ...Array.from(labelsTool.labels.values()),
                    // Drop saved labels that have a hydrated copy in the editable
                    // Map — their DOM overlay is rendered by the hydrated entry.
                    ...Array.from(serverArtifactRenderer.savedLabels.values()).filter(l => {
                        var _a, _b;
                        return !hiddenSavedLabelIds.has(l.id)
                            && ((_b = (_a = userFolders.derivedVisibility.get(l.id)) === null || _a === void 0 ? void 0 : _a.visible) !== null && _b !== void 0 ? _b : false);
                    }),
                ], hiddenIds: labelsTool.editingLabel ? new Set([labelsTool.editingLabel.labelId]) : undefined, onLabelClick: labelsTool.selectLabelById, onLabelDoubleClick: labelsTool.beginEditByLabelId, onLabelMouseDown: (id, e) => labelsTool.beginDragByLabelId(id, e), 
                // Show corner-drag resize handles on the selected label only when
                // we're not in the middle of an inline text edit (the textarea
                // already covers the same screen space).
                selectedLabelId: labelsTool.editingLabel ? undefined : (_35 = labelsTool.selectedLabel) === null || _35 === void 0 ? void 0 : _35.labelId, onResize: (id, newSize) => labelsTool.updateLabelStyle(id, { size: newSize }) }), (() => {
                const editing = labelsTool.editingLabel;
                if (!editing)
                    return null;
                const label = labelsTool.labels.get(editing.labelId);
                if (!label)
                    return null;
                return (_jsx(LabelInlineEditor, { ref: labelInlineEditorRef, viewer: viewer, label: label, screenPosition: editing.screenPosition, onCommit: (text) => {
                        setLabelEditorText('');
                        labelsTool.commitEditing(text);
                    }, onCancel: () => {
                        setLabelEditorText('');
                        labelsTool.cancelEditing();
                    }, onTextChange: setLabelEditorText, onResize: (id, newSize) => labelsTool.updateLabelStyle(id, { size: newSize }) }));
            })(), viewer && user && commentDrafts.map((entry) => {
                return (_jsx(CommentDraftInline, { viewer: viewer, draftAnchor: entry.draftAnchor, preLinkedArtifactId: entry.preLinkedArtifactId, currentUser: user, collaborators: projectCollaborators, tagSuggestions: projectCommentTags, onClose: () => closeDraft(entry.id), onSubmit: async (data) => {
                        await commentsApi.createComment({
                            content: data.content,
                            attachments: data.attachments,
                            artifactIds: data.artifactIds,
                            anchor: data.anchor,
                            mentionedUserIds: data.mentionedUserIds,
                            tags: data.tags,
                        });
                    } }, entry.id));
            }), viewer && commentPinsVisible && commentsApi.comments
                .filter(c => !c.parentCommentId && !c.deleted && c.anchor)
                .filter(c => {
                var _a;
                return !activeTagFilters || activeTagFilters.size === 0
                    || ((_a = c.tags) !== null && _a !== void 0 ? _a : []).some(t => activeTagFilters.has(t));
            })
                .filter(c => !commentThreads.some(t => t.threadCommentId === c.id))
                .map(c => (_jsx(CommentHtmlPin, { viewer: viewer, comment: c, onActivate: (clickPos) => openThreadAt(c.id, clickPos) }, c.id))), commentThreads.map((entry) => {
                var _a, _b;
                const thread = resolveThreadComment(entry.threadCommentId);
                if (!thread)
                    return null;
                const livePos = popoverPositions.get(entry.id);
                const liveSize = popoverSizes.get(entry.id);
                const anchor = thread.anchor
                    ? {
                        latitude: thread.anchor.latitude,
                        longitude: thread.anchor.longitude,
                        height: thread.anchor.height,
                    }
                    : null;
                return (_jsxs(React.Fragment, { children: [livePos && anchor && (_jsx(PopoverLeaderLine, { viewer: viewer, anchor: anchor, popoverPos: livePos, 
                            // Fall back to design defaults (360 × 520) for the first
                            // frame before ResizeObserver fires.
                            popoverWidth: (_a = liveSize === null || liveSize === void 0 ? void 0 : liveSize.width) !== null && _a !== void 0 ? _a : 360, popoverHeight: (_b = liveSize === null || liveSize === void 0 ? void 0 : liveSize.height) !== null && _b !== void 0 ? _b : 520 })), _jsx(CommentThreadPopover, { screenPosition: entry.screenPosition, thread: thread, tagSuggestions: projectCommentTags, currentUserId: user === null || user === void 0 ? void 0 : user.userId, onClose: () => closeThread(entry.id), onSizeChange: (size) => updatePopoverSize(entry.id, size), onPositionChange: (pos) => updatePopoverPosition(entry.id, pos), onReply: async (parentCommentId, content, artifactIds, mentionedUserIds, attachments) => {
                                await commentsApi.createComment({
                                    content,
                                    parentCommentId,
                                    artifactIds,
                                    mentionedUserIds,
                                    attachments,
                                });
                            }, onEdit: async (commentId, content, tags) => {
                                await commentsApi.updateComment(commentId, content, tags);
                            }, onDelete: async (commentId) => {
                                await commentsApi.deleteComment(commentId);
                                closeThread(entry.id);
                            } })] }, entry.id));
            }), (isLabelToolActive || labelsTool.selectedLabel || labelsTool.editingLabel) && (() => {
                var _a, _b, _c, _d, _e, _f;
                const selectedId = (_b = (_a = labelsTool.selectedLabel) === null || _a === void 0 ? void 0 : _a.labelId) !== null && _b !== void 0 ? _b : (_c = labelsTool.editingLabel) === null || _c === void 0 ? void 0 : _c.labelId;
                const target = selectedId ? labelsTool.labels.get(selectedId) : null;
                const style = target
                    ? {
                        size: target.size,
                        color: target.color,
                        stroke: target.stroke,
                        bold: target.bold,
                        italic: target.italic,
                        background: target.background,
                        fontFamily: target.fontFamily,
                        cornerRadius: (_d = target.cornerRadius) !== null && _d !== void 0 ? _d : 'rounded',
                        backgroundBorder: (_e = target.backgroundBorder) !== null && _e !== void 0 ? _e : false,
                    }
                    : defaultLabelStyle;
                // While the inline editor is open the label's stored text
                // hasn't been committed yet — fall back to the live typed
                // value so UnsavedSaveSection's Save button isn't stuck
                // disabled before Enter.
                const isEditingThis = !!labelsTool.editingLabel && target && labelsTool.editingLabel.labelId === target.id;
                const effectiveName = isEditingThis ? (labelEditorText || target.text) : ((_f = target === null || target === void 0 ? void 0 : target.text) !== null && _f !== void 0 ? _f : '');
                const commitFirst = (fn) => ((...args) => {
                    var _a;
                    if (isEditingThis) {
                        (_a = labelInlineEditorRef.current) === null || _a === void 0 ? void 0 : _a.commit();
                    }
                    // Defer to the next microtask so the commit's setState has
                    // flushed before downstream handlers read label.text.
                    setTimeout(() => fn(...args), 0);
                });
                const header = labelsTool.editingLabel
                    ? 'Editing label'
                    : labelsTool.selectedLabel
                        ? 'Label style'
                        : 'Click the map to place a label';
                return (_jsx(LabelStylePopout, { style: style, onStyleChange: (patch) => {
                        if (target) {
                            labelsTool.updateLabelStyle(target.id, patch);
                        }
                        else {
                            setDefaultLabelStyle(prev => ({ ...prev, ...patch }));
                        }
                    }, save: (target && (labelsTool.selectedLabel || labelsTool.editingLabel)) ? {
                        itemId: target.id,
                        // Live typed text while editing, committed text otherwise.
                        // UnsavedSaveSection uses this for Save-button gating and
                        // for the persisted artifact name.
                        currentName: effectiveName,
                        onChangeName: labelsTool.updateLabelName,
                        onRemove: labelsTool.removeLabel,
                        onClose: labelsTool.dismissSelection,
                        userFolders: userFolders.folders,
                        onCreateFolder: userFolders.createFolder,
                        // Save click flushes any in-flight inline edit first, then
                        // hands off to the artifact promotion flow. We deliberately
                        // read handleAddItemToFolder from a ref inside setTimeout so
                        // the deferred call uses the version rebuilt AFTER React
                        // commits the inline-editor's text/name setState — otherwise
                        // we'd read a stale labels Map and persist the auto-default
                        // "Label N" name instead of the typed text.
                        // The deferred call is wrapped in a promise so the save section
                        // still awaits the write it schedules: the label's form keeps
                        // its Save button pending until the round-trip finishes, and a
                        // refusal lands on the form instead of escaping the timer.
                        onSaveItem: (id, type, folderId) => {
                            var _a;
                            if (isEditingThis)
                                (_a = labelInlineEditorRef.current) === null || _a === void 0 ? void 0 : _a.commit();
                            return new Promise((resolve, reject) => {
                                setTimeout(() => {
                                    handleAddItemToFolderRef.current(id, type, folderId).then(resolve, reject);
                                }, 0);
                            });
                        },
                        // Hydrated copy of a saved label → save section swaps to
                        // "Save changes" / "Discard changes" and drops the folder
                        // picker (the artifact already lives in serverFolderId).
                        serverEdit: target.serverArtifactId
                            ? { serverFolderId: target.serverFolderId }
                            : undefined,
                    } : undefined, header: header, onDismiss: () => {
                        // Order matters: dropping the selection first ensures the
                        // render gate flips false on its own if the tool is also
                        // disarmed, so the pop-out closes cleanly in one render.
                        labelsTool.dismissSelection();
                        if (isLabelToolActive)
                            markupMode.setTool(null);
                    }, 
                    // Click-outside-closes only kicks in once the pop-out is in
                    // its "styling/saving an existing label" phase. Before
                    // placement (tool armed, no label yet) and during inline
                    // text entry, a stray canvas click must not yank the
                    // pop-out — placement and blur-to-commit need to run first.
                    suppressClickOutside: !labelsTool.selectedLabel || !!labelsTool.editingLabel, 
                    // Re-focus the inline editor's textarea after a <select>
                    // change so the user can keep typing without having to click
                    // back into the field.
                    onAfterStyleChange: () => { var _a; (_a = labelInlineEditorRef.current) === null || _a === void 0 ? void 0 : _a.focus(); } }));
            })(), grpcService.selectedProject && controlPanelState.viewMode === 'project-structure'
                && !isPolygonDrawing && !isDemUploading
                && !measureTool.pendingMeasurement && !measureTool.pendingCircle
                // Also hide while a markup polygon/path is being drawn so the draw HUD
                // doesn't sit on top of the toolbar (parity with project-boundary draw).
                && !polygonTool.isDrawing && !polygonTool.rectanglePending && !pathsTool.pendingPath && (() => {
                var _a, _b;
                // Capability gate: a client viewer's toolbar is comment + sketch +
                // Print only (hide-not-rebuild; OPA + ops guards enforce server-side).
                const pid = (_b = (_a = grpcService.selectedProject) === null || _a === void 0 ? void 0 : _a.getId) === null || _b === void 0 ? void 0 : _b.call(_a);
                const caps = capabilitiesForProject(userProjects.find((p) => p.id === pid));
                return (_jsx(CanvasBottomChrome, { activeTool: markupMode.activeTool, onSetTool: markupMode.setTool, onRequestUpload: (tool) => {
                        if (tool === 'path' || tool === 'polygon' || tool === 'placemark') {
                            setMarkupUploadTool(tool);
                        }
                    }, measureMode: measureTool.measureMode, onMeasureModeChange: measureTool.setMeasureMode, polygonDrawMode: polygonTool.polygonDrawMode, onPolygonDrawModeChange: polygonTool.setPolygonDrawMode, onPrint: caps.canPrint ? handlePrintRequest : undefined, visibleTools: caps.tools }));
            })(), markupUploadTool && (_jsx(MarkupUploadModal, { tool: markupUploadTool, isOpen: true, onClose: () => setMarkupUploadTool(null), onUpload: (file) => {
                    const dt = new DataTransfer();
                    dt.items.add(file);
                    if (markupUploadTool === 'path') {
                        promotion.handlePathFileUpload(dt.files);
                    }
                    else if (markupUploadTool === 'polygon') {
                        promotion.handlePolygonFileUpload(dt.files);
                    }
                    else if (markupUploadTool === 'placemark') {
                        promotion.handlePlacemarkFileUpload(dt.files);
                    }
                }, onPasteCoordinates: markupUploadTool === 'placemark'
                    ? (coords) => {
                        coords.forEach((c) => placemarksTool.addPlacemark(c.lat, c.lng, c.name));
                    }
                    : undefined })), artifactEditSaveError && (_jsxs("div", { className: "fixed bottom-32 left-1/2 -translate-x-1/2 z-[70] flex items-center gap-2 px-3 py-2 rounded-lg shadow-2xl max-w-[90vw]", style: { backgroundColor: 'var(--charcoal)', border: '1px solid #DC143C' }, children: [_jsx("span", { className: "text-xs", style: { color: '#DC143C' }, children: artifactEditSaveError }), _jsx("button", { onClick: () => setArtifactEditSaveError(null), className: "px-1 rounded text-xs", style: { color: 'var(--dew)', background: 'none', border: 'none' }, title: "Dismiss", "aria-label": "Dismiss", children: _jsx(X, { size: 14 }) })] })), isSketchToolActive && (_jsx(SketchToolPanel, { mode: sketchMode, onModeChange: setSketchMode, color: sketchColor, onColorChange: setSketchColor, projectColors: projectColors, width: sketchWidth, onWidthChange: setSketchWidth, opacity: sketchOpacity, onOpacityChange: setSketchOpacity, eraserSize: sketchEraserSize, onEraserSizeChange: setSketchEraserSize, strokeCount: sketchTool.strokeCount, isDrawing: sketchTool.isDrawing, canUndo: sketchTool.canUndo, onUndo: sketchTool.undo, onClear: sketchTool.clearAll, onFinish: () => setSketchSaveOpen(true), isEditing: isEditingSketch, editingName: editingSketchName, onSaveEdit: handleSaveSketchEdit, onCancelEdit: handleCancelSketchEdit })), isSketchToolActive && (_jsx(SketchSaveModal, { isOpen: sketchSaveOpen, strokeCount: sketchTool.strokeCount, userFolders: userFolders.folders, onCreateFolder: userFolders.createFolder, onSave: handleSaveSketch, onDiscard: () => { sketchTool.resetCanvas(); setSketchSaveOpen(false); }, onClose: () => setSketchSaveOpen(false), defaultFolderColor: sketchColor })), markupMode.activeTool && (_jsx(ToolDrawPanel, { activeTool: markupMode.activeTool, pathIsDrawing: !!pathsTool.pendingPath, pathPointCount: (_38 = (_37 = (_36 = pathsTool.pendingPath) === null || _36 === void 0 ? void 0 : _36.points) === null || _37 === void 0 ? void 0 : _37.length) !== null && _38 !== void 0 ? _38 : 0, pathDistanceM: (_41 = (_40 = (_39 = pathsTool.pendingPath) === null || _39 === void 0 ? void 0 : _39.segments) === null || _40 === void 0 ? void 0 : _40.reduce((sum, s) => sum + s.distanceMeters, 0)) !== null && _41 !== void 0 ? _41 : 0, onFinishPath: () => {
                    const bc = bufferEnabled && bufferDistance && !bufferError
                        ? { distance: parseFloat(bufferDistance), unit: bufferUnit } : undefined;
                    pathsTool.finishPath(undefined, bc);
                }, onCancelPath: pathsTool.cancelPending, onUndoPathPoint: pathsTool.undoLastPoint, onImportPath: (file) => {
                    const dt = new DataTransfer();
                    dt.items.add(file);
                    promotion.handlePathFileUpload(dt.files);
                }, polygonIsDrawing: polygonTool.isDrawing, polygonPointCount: polygonTool.drawingPointCount, polygonAreaM2: polygonTool.drawingAreaM2, polygonDrawMode: polygonTool.polygonDrawMode, polygonRectanglePending: polygonTool.rectanglePending, drawLabelsEnabled: markupMode.activeTool === 'path' ? pathsTool.showSegmentLabels : polygonTool.showDrawLabels, onToggleDrawLabels: markupMode.activeTool === 'path'
                    ? () => pathsTool.setShowSegmentLabels(!pathsTool.showSegmentLabels)
                    : () => polygonTool.setShowDrawLabels(!polygonTool.showDrawLabels), onFinishPolygon: () => polygonTool.finishPolygon(), onCancelPolygon: polygonTool.cancelPending, onUndoPolygonPoint: polygonTool.undoLastPoint, measureIsActive: isMeasureToolActive, measureHasPoints: !!measureTool.pendingMeasurement || !!measureTool.pendingCircle, onCancelMeasure: measureTool.cancelPending, measureMode: measureTool.measureMode, measureLiveDistanceM: (_42 = measureTool.drawingHUD) === null || _42 === void 0 ? void 0 : _42.rawDistanceMeters, measureLiveBearingDeg: (_43 = measureTool.drawingHUD) === null || _43 === void 0 ? void 0 : _43.rawBearingDeg, measureLiveRadiusM: (_44 = measureTool.drawingHUD) === null || _44 === void 0 ? void 0 : _44.rawRadiusMeters, measureLiveAreaM2: (_45 = measureTool.drawingHUD) === null || _45 === void 0 ? void 0 : _45.rawAreaSqM, contourIsActive: isContourToolActive, contourHighlightCount: (_47 = (_46 = contourHighlight.highlightedContours) === null || _46 === void 0 ? void 0 : _46.size) !== null && _47 !== void 0 ? _47 : 0, contourLayers: contourLayers, streamIsActive: isStreamToolActive, streamHighlightCount: (_49 = (_48 = streamHighlight.highlightedStreams) === null || _48 === void 0 ? void 0 : _48.size) !== null && _49 !== void 0 ? _49 : 0, streamLayers: streamLayers, bufferConfig: (markupMode.activeTool === 'path' || markupMode.activeTool === 'stream')
                    && !serverArtifactEdit.isEditing
                    && !pathsTool.selectedPath
                    && !polygonTool.selectedPolygon
                    && !placemarksTool.selectedPlacemark
                    && !measureTool.selectedMeasurement
                    && !labelsTool.selectedLabel
                    && !serverArtifactRenderer.selectedArtifact
                    && pathsTool.poppedOutProfiles.length === 0
                    ? bufferConfig : undefined, onBufferEnabledChange: buffer.setBufferEnabled, onBufferDistanceChange: buffer.setBufferDistance, onBufferUnitChange: buffer.handleBufferUnitChange, onToggleLayer: (layerId) => {
                    var _a;
                    const isLoaded = grpcService.loadedArtifacts.has(layerId);
                    if (!isLoaded) {
                        const artifactMetadata = (_a = grpcService.artifacts) === null || _a === void 0 ? void 0 : _a.find((a) => a.getId() === layerId);
                        if (artifactMetadata) {
                            grpcService.loadArtifact(layerId, artifactMetadata);
                        }
                    }
                    else {
                        const visible = grpcService.isArtifactVisible(layerId);
                        grpcService.toggleArtifact(layerId, !visible);
                    }
                }, externalAreaUnit: unitPreferences.areaUnit, onAreaUnitChange: unitPreferences.setAreaUnit, externalDistanceUnit: unitPreferences.distanceUnit, onDistanceUnitChange: unitPreferences.setDistanceUnit, pathSegmentUnit: pathSegmentUnit, onPathSegmentUnitChange: setPathSegmentUnit, pathTotalUnit: pathTotalUnit, onPathTotalUnitChange: setPathTotalUnit, unitSystem: unitPreferences.system, onSystemChange: unitPreferences.setSystem, hasSelectedVertex: markupMode.activeTool === 'path'
                    ? !!pathsTool.selectedDrawingVertex
                    : markupMode.activeTool === 'polygon'
                        ? polygonTool.selectedDrawingVertexIndex >= 0
                        : false, onDeleteSelectedVertex: markupMode.activeTool === 'path'
                    ? pathsTool.deleteSelectedDrawingVertex
                    : markupMode.activeTool === 'polygon'
                        ? polygonTool.deleteSelectedDrawingVertex
                        : undefined, hideImport: serverArtifactEdit.isEditing
                    || !!pathsTool.selectedPath
                    || !!polygonTool.selectedPolygon
                    || !!placemarksTool.selectedPlacemark
                    || !!measureTool.selectedMeasurement
                    || !!labelsTool.selectedLabel
                    || !!serverArtifactRenderer.selectedArtifact
                    || pathsTool.poppedOutProfiles.length > 0, isEditing: serverArtifactEdit.isEditing, editingItemName: editingItemName, onSaveEdit: () => {
                    const s = serverArtifactEdit.editingState;
                    if (!(s === null || s === void 0 ? void 0 : s.unsavedItemId))
                        return;
                    markupMode.setTool(null);
                    runServerArtifactSave(() => promotion.handleAddItemToFolder(s.unsavedItemId, s.artifactType, s.folderId));
                }, onCancelEdit: () => {
                    const s = serverArtifactEdit.editingState;
                    if (!(s === null || s === void 0 ? void 0 : s.unsavedItemId))
                        return;
                    markupMode.setTool(null);
                    unsavedFacade.handleDeleteUnsavedItem(s.unsavedItemId, s.artifactType);
                } })), _jsx(ServerArtifactDialogs, { serverArtifactRenderer: serverArtifactRenderer, serverArtifactView: serverArtifactView, serverArtifactPolygonStats: serverArtifactPolygonStats, projectId: ((_51 = (_50 = grpcService.selectedProject) === null || _50 === void 0 ? void 0 : _50.getId) === null || _51 === void 0 ? void 0 : _51.call(_50)) || null, handleProfileHoverPosition: handleProfileHoverPosition, onUpdateArtifactName: userFolders.updateArtifactName, onUpdateArtifactShowLabel: userFolders.updateArtifactShowLabel, unitPreferences: unitPreferences, serverArtifactEdit: serverArtifactEdit, currentUserId: user === null || user === void 0 ? void 0 : user.userId, isClientViewer: selectedProjectCaps.isClientViewer }), ((_52 = serverArtifactEdit.editingState) === null || _52 === void 0 ? void 0 : _52.artifactType) === 'path' && editingPathId
                && (((_53 = pathsTool.selectedPath) === null || _53 === void 0 ? void 0 : _53.pathId) !== editingPathId || pathsTool.pathDialogHidden) && (_jsxs("div", { className: "fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 px-3 py-2 rounded-lg shadow-2xl", style: { backgroundColor: 'var(--charcoal)', border: '1px solid var(--pewter)' }, children: [_jsxs("span", { className: "text-xs", style: { color: 'var(--dew)' }, children: ["Editing ", _jsx("span", { style: { color: 'var(--sand)' }, children: (_55 = (_54 = pathsTool.paths.get(editingPathId)) === null || _54 === void 0 ? void 0 : _54.name) !== null && _55 !== void 0 ? _55 : 'path' })] }), _jsx("button", { onClick: () => pathsTool.selectPathById(editingPathId), className: "px-2 py-1 rounded text-xs", style: { color: 'var(--dew)', border: '1px solid var(--pewter)' }, title: "Reopen the path details dialog", children: "Details" }), _jsx("button", { onClick: handleCancelPathServerEdit, className: "px-2 py-1 rounded text-xs", style: { color: 'var(--dew)', border: '1px solid var(--pewter)' }, title: "Discard changes and exit editing", children: "Cancel" }), _jsx("button", { onClick: handleSavePathServerEdit, className: "px-2 py-1 rounded text-xs font-medium", style: { backgroundColor: 'var(--sand)', color: 'var(--forest)' }, title: "Save changes to the path", children: "Save" })] })), pathsTool.poppedOutProfiles.map(popout => {
                var _a, _b, _c, _d;
                const path = pathsTool.paths.get(popout.pathId);
                if (!path)
                    return null;
                const layoutKey = `${popout.pathId}:${(_a = popout.segmentIndex) !== null && _a !== void 0 ? _a : 'total'}`;
                // Header Save affordance: a saved path mid-edit commits directly;
                // a fresh unsaved path opens the details dialog (folder pick lives
                // there). View-mode copies are read-only — no save.
                const isEditCopy = editingPathId === popout.pathId;
                const isViewCopy = ((_b = serverArtifactEdit.viewingState) === null || _b === void 0 ? void 0 : _b.artifactType) === 'path'
                    && serverArtifactEdit.viewingState.unsavedItemId === popout.pathId;
                return (_jsx(PathElevationPopout, { path: path, segmentIndex: popout.segmentIndex, onLayoutChange: (layout) => { pathPopoutLayoutsRef.current.set(layoutKey, layout); }, onClose: () => {
                        pathPopoutLayoutsRef.current.delete(layoutKey);
                        pathsTool.closePopOutProfile(popout.pathId, popout.segmentIndex);
                    }, onUnitChange: (unit) => pathsTool.updatePathUnit(popout.pathId, unit), onHighlightSegment: pathsTool.highlightSegment, initialPosition: popout.position, onHoverPosition: handleProfileHoverPosition, externalCrosshairFraction: ((_c = pathsTool.hoveredPath) === null || _c === void 0 ? void 0 : _c.pathId) === popout.pathId ? pathCrosshairFraction : null, onChangeColorBy: pathsTool.updatePathColorBy, onSave: isEditCopy ? handleSavePathServerEdit : undefined, inlineSave: isEditCopy || isViewCopy ? undefined : {
                        userFolders: userFolders.folders,
                        onCreateFolder: userFolders.createFolder,
                        onSaveItem: promotion.handleAddItemToFolder,
                        onChangeName: pathsTool.updatePathName,
                        onRemove: pathsTool.removePath,
                    }, saveLabel: isEditCopy ? 'Save' : 'Save…', saveTitle: isEditCopy
                        ? 'Save changes to this path'
                        : 'Save this path to a folder' }, `${popout.pathId}-${(_d = popout.segmentIndex) !== null && _d !== void 0 ? _d : 'total'}`));
            }), !isInitialized && (_jsx("div", { className: "absolute inset-0 flex items-center justify-center z-50", style: { backgroundColor: 'var(--charcoal)' }, children: _jsxs("div", { className: "text-center text-white", children: [_jsx("p", { className: "font-heading text-2xl font-bold mb-2", style: { color: 'var(--sand)' }, children: "LandScope" }), _jsx("p", { className: "font-body text-lg font-light", style: { color: 'var(--dew)' }, children: "Clarity before commitment" })] }) })), _jsx(AccountModal, { isOpen: modalState.activeModal === 'account', onClose: () => { setAccountInitialData(null); modalActions.closeModal(); }, onSwitchToLogin: () => { setAccountInitialData(null); modalActions.switchAccountToLogin(); }, onEmailVerificationSet: handleEmailVerificationSet, initialData: accountInitialData !== null && accountInitialData !== void 0 ? accountInitialData : undefined }), _jsx(AccountInfoModal, { isOpen: modalState.activeModal === 'accountInfo', initialTab: modalState.accountInfoInitialTab, onClose: () => modalActions.closeModal() }), _jsx(LoginModal, { isOpen: modalState.activeModal === 'login', onClose: () => modalActions.closeModal() }), _jsx(CreateProjectModal, { isOpen: modalState.activeModal === 'createProject', onClose: () => modalActions.closeModal(), onStartPolygonDrawing: handleStartPolygonDrawing, isDemUploading: isDemUploading, initialDemData: initialDemData, onClearInitialDemData: () => setInitialDemData(null) }), _jsx(CreateProjectConfirmation, { isOpen: modalState.activeModal === 'confirmCreate', onClose: () => modalActions.closeModal(), projectName: ((_56 = modalState.confirmCreateData) === null || _56 === void 0 ? void 0 : _56.projectName) || '', onProjectCreated: () => modalActions.closeModal() }), _jsx(DemoGateOnCreateModal, {}), _jsx(GlobalAtlasModal, {}), _jsx(KeyboardShortcutsModal, {}), _jsx(PolygonDrawing, { viewer: viewer, isActive: isPolygonDrawing, projectName: polygonProjectName, projectDescription: polygonProjectDescription, isAccountModalOpen: modalState.activeModal === 'account', isLoginModalOpen: modalState.activeModal === 'login', currentBasemap: grpcService.currentBasemap, onChangeBasemap: grpcService.changeBasemap, onFinish: handlePolygonDrawingFinish, onCancel: handlePolygonDrawingCancel, onShowAccountModal: (data) => { setAccountInitialData(data !== null && data !== void 0 ? data : null); modalActions.openAccount(); }, onShowCreateProjectModal: () => modalActions.openCreateProject() })] }));
};
// Wrapper component that handles both with and without props
export const MapViewer = (props) => {
    return (_jsx(ControlPanelProvider, { children: _jsx(MapViewerComponent, { ...props }) }));
};
